import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Tooltip,
  Fade,
  Skeleton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalculateIcon from '@mui/icons-material/Calculate';
import { Trade, TradeType, DashboardSummary, Client, Employee } from '../types';
import { tradeService } from '../services/trade.service';
import { analyticsService } from '../services/analytics.service';
import { clientService } from '../services/client.service';
import { salaryService } from '../services/salary.service';
import { TradeDialog } from '../components/common/TradeDialog';
import CommissionCalculator from '../components/common/CommissionCalculator';
import { marketService } from '../services/market.service';
import { applyLIFO, computeBreakevenForPosition, BreakevenResult, TradeLeg, calculateChargesBreakdown, DEFAULT_OPTION_BROKERAGE } from '../hooks/useTradeCalculations';
import { CHARGE_RATES } from '../utils/constants';
import { getFuturesSLAmount } from '../utils/stopLoss';
import { dailyBrokerageContribution } from '../utils/brokerage';
import { istDay } from '../utils/formatters';

// ============================================================================
// CONSTANTS & COLORS
// ============================================================================

const COLORS = {
  primary: '#0A0A0A',
  primaryLight: '#404040',
  primaryDark: '#000000',
  secondary: '#525252',
  positive: '#15803D',
  positiveLight: '#DCFCE7',
  negativeDark: '#991B1B',
  negative: '#B91C1C',
  negativeLight: '#FEE2E2',
  warning: '#B45309',
  warningLight: '#FEF3C7',
  blue: '#1D4ED8',
  blueLight: '#DBEAFE',
  pageBg: '#F4F4F5',
  cardBg: '#FFFFFF',
  tableBg: '#F4F4F5',
  textPrimary: '#0A0A0A',
  textSecondary: '#737373',
  border: '#E5E5E5',
};

const formatCurrency = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(val);

const formatCurrencyFull = (val: number): string =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val);

const formatPercent = (val: number): string =>
  `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;

// formatNumber removed - no longer used after top cards removal

type PeriodView = 'day' | 'week' | 'month' | 'year';
type SortDirection = 'asc' | 'desc';
interface SortConfig { key: string; direction: SortDirection; }

interface MergedPosition {
  key: string;                  // clientId__scrip__normalizedType__direction
  clientId: string;
  clientName: string;
  scrip: string;
  tradeType: TradeType;
  direction: 'BUY' | 'SELL';
  balanceLots: number;          // total remaining lots (for F&O) or shares (for equity where lotSize=1)
  lotSize: number;
  shares: number;               // balanceLots * lotSize
  weightedAvgBuyPrice: number;
  investedAmount: number;
  currentValue: number;
  currentPrice: number;
  unrealizedPnL: number;
  realizedPnL: number;
  totalPnL: number;
  pnlPercentage: number;
  breakeven: BreakevenResult;
  tradeLegs: Trade[];           // original individual trades
  legSummary: string;           // "Bought 10@120, Sold 6@130" etc.
  serialNumber: number;
  createdAt: string;
  isMTF: boolean;
  leverage?: string;
  deployedAmount: number;  // Client's deployed amount frozen at trade open
}

function isEquityType(t: string): boolean {
  return t === 'Stock' || t === 'Equity' || t === 'Intra Stock';
}
function isFutureType(t: string): boolean {
  return t === 'Future';
}
function isOptionType(t: string): boolean {
  return t === 'Option';
}

function normalizeTradeType(t: TradeType): string {
  if (t === TradeType.STOCK || t === TradeType.EQUITY || t === TradeType.INTRA_STOCK) return 'equity';
  if (t === TradeType.FUTURE) return 'future';
  return 'option';
}

function isEquityTradeType(t: TradeType): boolean {
  return t === TradeType.STOCK || t === TradeType.EQUITY || t === TradeType.INTRA_STOCK;
}

function mergeOpenPositions(trades: Trade[], clients: Client[]): MergedPosition[] {
  const clientMap = new Map(clients.map(c => [c.clientId, c]));
  const openTrades = trades.filter(t => t.status === 'OPEN' && !t.isDeleted);

  // Group by clientId + scrip + normalizedType
  // For options: exact scrip match (NIFTY 24500 CE ≠ NIFTY 24800 CE)
  // For equity: normalize Stock/Equity/Intra → "equity"
  // For future: exact scrip match
  const groups = new Map<string, Trade[]>();

  for (const t of openTrades) {
    const normType = normalizeTradeType(t.tradeType);
    const dir = t.direction || 'BUY';
    const key = `${t.clientId}__${t.scrip}__${normType}__${dir}`;
    const group = groups.get(key) || [];
    group.push(t);
    groups.set(key, group);
  }

  const merged: MergedPosition[] = [];
  let serial = 1;

  for (const [key, groupTrades] of groups) {
    const first = groupTrades[0];
    const client = clientMap.get(first.clientId);
    const brokerageRates = client?.brokerageRates || { stock: 0.003, option: 100, intraStock: 0.0003 };

    // Build trade legs
    const direction: 'BUY' | 'SELL' = (first.direction as 'BUY' | 'SELL') || 'BUY';
    const isSellFirst = direction === 'SELL';
    const legs: TradeLeg[] = groupTrades.map(t => ({
      tradeId: t.tradeId,
      lots: t.purchaseLots,
      lotSize: t.lotSize,
      price: t.purchasePrice,
      date: t.date || t.createdAt,
      tradeType: t.tradeType,
      saleLots: t.saleLots,
      salePrice: t.salePrice,
      direction: t.direction as 'BUY' | 'SELL' | undefined,
    }));

    // Total sold shares across all trades in this group
    const totalSoldShares = groupTrades.reduce((sum, t) => sum + (t.saleLots || 0) * (t.lotSize || 1), 0);

    // Apply LIFO
    const lifo = applyLIFO(legs, totalSoldShares);

    if (lifo.totalRemainingShares <= 0) continue; // fully sold

    // Current price (use latest trade's currentPrice or purchasePrice)
    const latestTrade = [...groupTrades].sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime())[0];
    const cmp = latestTrade.currentPrice || latestTrade.purchasePrice;

    const invested = lifo.totalRemainingCost;
    const currentValue = cmp * lifo.totalRemainingShares;
    // For sell-first: profit when price drops (invested = open sell revenue for remaining)
    const unrealizedPnL = isSellFirst ? (invested - currentValue) : (currentValue - invested);
    const realizedPnL = groupTrades.reduce((sum, t) => sum + (t.realizedPnL || 0), 0);
    const totalPnL = realizedPnL + unrealizedPnL;
    const totalOrigInvestment = groupTrades.reduce((sum, t) => sum + t.purchaseLots * t.lotSize * t.purchasePrice, 0);
    const pnlPct = totalOrigInvestment > 0 ? (totalPnL / totalOrigInvestment) * 100 : 0;

    // Compute breakeven
    const primaryType = isEquityTradeType(first.tradeType) ? TradeType.EQUITY : first.tradeType;
    const breakeven = computeBreakevenForPosition(lifo.remainingLegs, brokerageRates, primaryType, client?.name, direction);

    // Build leg summary string
    const openLabel = isSellFirst ? 'Sell' : 'Buy';
    const closeLabel = isSellFirst ? 'Cover' : 'Sell';
    const legParts: string[] = [];
    for (const t of groupTrades.sort((a, b) => new Date(a.date || a.createdAt).getTime() - new Date(b.date || b.createdAt).getTime())) {
      const qty = t.purchaseLots * t.lotSize;
      legParts.push(`${openLabel} ${qty}@${t.purchasePrice}`);
      if (t.saleLots > 0) {
        legParts.push(`${closeLabel} ${t.saleLots * t.lotSize}@${t.salePrice}`);
      }
    }

    // Determine lotSize (for F&O it matters, for equity it's 1)
    const lotSize = first.lotSize || 1;
    const balanceLots = isEquityTradeType(first.tradeType) ? lifo.totalRemainingShares : Math.round(lifo.totalRemainingShares / lotSize);

    merged.push({
      key,
      clientId: first.clientId,
      clientName: first.clientName,
      scrip: first.scrip,
      tradeType: primaryType,
      direction,
      balanceLots,
      lotSize,
      shares: lifo.totalRemainingShares,
      weightedAvgBuyPrice: lifo.weightedAvgBuyPrice,
      investedAmount: invested,
      currentValue,
      currentPrice: cmp,
      unrealizedPnL,
      realizedPnL,
      totalPnL,
      pnlPercentage: Math.round(pnlPct * 100) / 100,
      breakeven,
      tradeLegs: groupTrades,
      legSummary: legParts.join(', '),
      serialNumber: serial++,
      createdAt: groupTrades.sort((a, b) => new Date(b.createdAt || b.date).getTime() - new Date(a.createdAt || a.date).getTime())[0]?.createdAt || groupTrades[0]?.date || '',
      isMTF: groupTrades.some(t => t.isMTF),
      leverage: groupTrades.find(t => t.isMTF)?.leverage,
      // Frozen deployed amount from the earliest trade leg (or fallback to client data)
      deployedAmount: (() => {
        const sorted = [...groupTrades].sort((a, b) => new Date(a.date || a.createdAt).getTime() - new Date(b.date || b.createdAt).getTime());
        const fromTrade = sorted[0]?.deployedAmount;
        if (fromTrade && fromTrade > 0) return fromTrade;
        // Fallback for pre-migration trades
        return client?.currentDeployedAmount ?? client?.initialCapital ?? 0;
      })(),
    });
  }

  return merged.sort((a, b) => b.investedAmount - a.investedAmount);
}

// ============================================================================
// PERIOD RANGE HELPERS
// ============================================================================

function getPeriodRange(view: PeriodView): { start: Date; end: Date; label: string } {
  // Build "today in IST" from the current instant, regardless of the user's
  // browser timezone. We get the IST Y/M/D components, then construct Date
  // objects that — when compared via `getTime()` against the IST instant of
  // a trade — produce the right calendar-day boundaries.
  const istNow = new Date(new Date().getTime() + 5.5 * 60 * 60 * 1000);
  const y = istNow.getUTCFullYear();
  const m = istNow.getUTCMonth();
  const d = istNow.getUTCDate();
  const today = new Date(Date.UTC(y, m, d));

  if (view === 'day') {
    return { start: today, end: new Date(today.getTime() + 86400000 - 1), label: today.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }) };
  }
  if (view === 'week') {
    const dow = today.getUTCDay();
    const mon = new Date(today); mon.setUTCDate(today.getUTCDate() - (dow === 0 ? 6 : dow - 1));
    const sun = new Date(mon); sun.setUTCDate(mon.getUTCDate() + 7); sun.setUTCMilliseconds(-1);
    return { start: mon, end: sun, label: `${mon.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' })} - ${sun.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' })}` };
  }
  if (view === 'year') {
    const fyStart = m >= 3 ? new Date(Date.UTC(y, 3, 1)) : new Date(Date.UTC(y - 1, 3, 1));
    const fyEnd = new Date(Date.UTC(fyStart.getUTCFullYear() + 1, 2, 31, 23, 59, 59, 999));
    return { start: fyStart, end: fyEnd, label: `FY ${fyStart.getUTCFullYear()}-${fyEnd.getUTCFullYear() % 100}` };
  }
  // month: 26th-25th cycle (in IST)
  const cs = d >= 26 ? new Date(Date.UTC(y, m, 26)) : new Date(Date.UTC(y, m - 1, 26));
  const ce = d >= 26 ? new Date(Date.UTC(y, m + 1, 25, 23, 59, 59, 999)) : new Date(Date.UTC(y, m, 25, 23, 59, 59, 999));
  return { start: cs, end: ce, label: `${cs.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' })} - ${ce.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' })}` };
}

function isTradeInPeriod(tradeDate: string, start: Date, end: Date): boolean {
  // Compare IST-shifted trade instant against IST-anchored start/end (which
  // were built as UTC midnight of the IST calendar day).
  const d = new Date(new Date(tradeDate).getTime() + 5.5 * 60 * 60 * 1000);
  return d >= start && d <= end;
}

// ============================================================================
// STAT CARD
// ============================================================================

// ============================================================================
// DEALER REVENUE POPUP
// ============================================================================

function DealerRevenuePopup({ open, onClose, trades, clients }: { open: boolean; onClose: () => void; trades: Trade[]; clients: Client[] }) {
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(false);
  const [expandedDealer, setExpandedDealer] = useState<string | null>(null);
  const [popupPeriod, setPopupPeriod] = useState<'day' | 'week' | 'month' | 'year' | 'all'>('day');

  // Period filter: include any trade whose buy day OR sell day falls in the
  // selected window. Daily-attribution rule means a single trade can
  // contribute brokerage to two different days.
  const periodFilteredTrades = useMemo(() => {
    const nonDeleted = trades.filter(t => !t.isDeleted);
    if (popupPeriod === 'all') return nonDeleted;
    const range = getPeriodRange(popupPeriod);
    return nonDeleted.filter(t => {
      const buyIn = isTradeInPeriod(t.date || t.createdAt || '', range.start, range.end);
      const sellIn = (t.saleLots || 0) > 0 &&
        isTradeInPeriod(t.saleDate || t.date || t.createdAt || '', range.start, range.end);
      return buyIn || sellIn;
    });
  }, [trades, popupPeriod]);

  const dealerData = useMemo(() => {
    const cMap = new Map(clients.map(c => [c.clientId, c]));
    const range = popupPeriod === 'all' ? null : getPeriodRange(popupPeriod);
    const startStr = range ? istDay(range.start.toISOString()) : '0000-00-00';
    const endStr = range ? istDay(range.end.toISOString()) : '9999-99-99';
    const dMap = new Map<string, Map<string, { clientName: string; revenue: number; tradeCount: number; turnover: number; openTrades: number }>>();
    periodFilteredTrades.forEach(t => {
      const cl = cMap.get(t.clientId);
      const dealer = cl?.referredByName || 'Direct';
      if (!dMap.has(dealer)) dMap.set(dealer, new Map());
      const clientsMap = dMap.get(dealer)!;
      const cKey = t.clientId;
      if (!clientsMap.has(cKey)) clientsMap.set(cKey, { clientName: t.clientName || cKey, revenue: 0, tradeCount: 0, turnover: 0, openTrades: 0 });
      const entry = clientsMap.get(cKey)!;
      const buyVal = t.purchaseLots * t.lotSize * t.purchasePrice;
      const sellVal = (t.saleLots || 0) * t.lotSize * (t.salePrice || 0);
      const turnover = buyVal + sellVal;
      // Bajaj-style daily attribution: buy commission books on buy day, sell
      // commission books on sell day. Sum only what falls in the period.
      let rev = 0;
      if (cl) {
        const c = dailyBrokerageContribution(t, cl.brokerageRates as any);
        if (c.buyDay >= startStr && c.buyDay <= endStr) rev += c.buy;
        if (c.sellDay && c.sellDay >= startStr && c.sellDay <= endStr) rev += c.sell;
      }
      entry.revenue += rev;
      entry.tradeCount += 1;
      entry.turnover += turnover;
      if (t.status === 'OPEN') entry.openTrades += 1;
    });
    return Array.from(dMap.entries()).map(([dealer, clientsMap]) => {
      const clientRows = Array.from(clientsMap.values()).sort((a, b) => b.revenue - a.revenue);
      const totalRevenue = clientRows.reduce((s, c) => s + c.revenue, 0);
      const totalTurnover = clientRows.reduce((s, c) => s + c.turnover, 0);
      return { dealer, clients: clientRows, totalRevenue, totalTurnover, totalTrades: clientRows.reduce((s, c) => s + c.tradeCount, 0), totalOpen: clientRows.reduce((s, c) => s + c.openTrades, 0), clientCount: clientRows.length };
    }).sort((a, b) => b.totalRevenue - a.totalRevenue);
  }, [periodFilteredTrades, clients]);
  const grandTotal = dealerData.reduce((s, d) => s + d.totalRevenue, 0);
  const maxRevenue = Math.max(...dealerData.map(d => d.totalRevenue), 1);

  return (
    <Dialog open={open} onClose={() => { onClose(); setShowDetails(false); setExpandedDealer(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, mx: 'auto' } }} sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: COLORS.primary, color: '#fff', py: 1.5, px: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>{showDetails ? 'Dealer Details' : 'Dealer Revenue'}</Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {showDetails && (
            <Button size="small" onClick={() => { setShowDetails(false); setExpandedDealer(null); }} sx={{ color: '#fff', textTransform: 'none', fontSize: '0.75rem' }}>← Back</Button>
          )}
          <IconButton size="small" onClick={() => { onClose(); setShowDetails(false); setExpandedDealer(null); }} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, borderBottom: `1px solid ${COLORS.border}` }}>
          <ToggleButtonGroup
            value={popupPeriod}
            exclusive
            onChange={(_, v) => { if (v) setPopupPeriod(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, px: 1.5, py: 0.3 } }}
          >
            <ToggleButton value="day">D</ToggleButton>
            <ToggleButton value="week">W</ToggleButton>
            <ToggleButton value="month">M</ToggleButton>
            <ToggleButton value="year">Y</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {!showDetails ? (
          /* ===== SIMPLE VIEW: Dealer-level only ===== */
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Dealer', 'Commission', 'Trades', 'Open', 'Turnover'].map(h => (
                      <TableCell key={h} align={h === 'Dealer' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.7rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB', py: 1.2 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dealerData.map(d => (
                    <TableRow key={d.dealer} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: `${COLORS.primary}06` } }} onClick={() => { setShowDetails(true); setExpandedDealer(d.dealer); }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: COLORS.textPrimary }}>{d.dealer}</Typography>
                          <Chip label={`${d.clientCount}`} size="small" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, bgcolor: `${COLORS.primary}12`, color: COLORS.primary }} />
                        </Box>
                        {/* Mini revenue bar */}
                        <Box sx={{ mt: 0.5, height: 4, bgcolor: '#f0f0f0', borderRadius: 2, overflow: 'hidden', maxWidth: 150 }}>
                          <Box sx={{ width: `${(d.totalRevenue / maxRevenue) * 100}%`, height: '100%', background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.positive})`, borderRadius: 2 }} />
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, color: COLORS.positive, fontSize: '0.9rem' }}>{formatCurrency(d.totalRevenue)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{d.totalTrades}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem', color: COLORS.blue }}>{d.totalOpen}</TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.textSecondary }}>{formatCurrency(d.totalTurnover)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: '#F0F0FF' }}>
                    <TableCell sx={{ fontWeight: 800, fontSize: '0.85rem' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: COLORS.primary, fontSize: '0.9rem' }}>{formatCurrency(grandTotal)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{dealerData.reduce((s, d) => s + d.totalTrades, 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.blue }}>{dealerData.reduce((s, d) => s + d.totalOpen, 0)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(dealerData.reduce((s, d) => s + d.totalTurnover, 0))}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ p: 1.5, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.7rem' }}>Click a dealer row to see client details</Typography>
            </Box>
          </>
        ) : (
          /* ===== DETAIL VIEW: Expanded dealer with client breakdown ===== */
          (() => {
            const d = dealerData.find(dd => dd.dealer === expandedDealer);
            if (!d) return null;
            return (
              <>
                {/* Dealer summary header */}
                <Box sx={{ p: 2, bgcolor: `${COLORS.primary}08`, borderBottom: `1px solid ${COLORS.border}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: COLORS.primary }}>{d.dealer}</Typography>
                    <Chip label={`${d.clientCount} clients`} size="small" sx={{ fontWeight: 700, bgcolor: `${COLORS.primary}15`, color: COLORS.primary }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {[
                      { l: 'Commission', v: formatCurrency(d.totalRevenue), c: COLORS.positive },
                      { l: 'Trades', v: d.totalTrades.toString(), c: COLORS.textPrimary },
                      { l: 'Open', v: d.totalOpen.toString(), c: COLORS.blue },
                      { l: 'Turnover', v: formatCurrency(d.totalTurnover), c: COLORS.textSecondary },
                    ].map(item => (
                      <Box key={item.l}>
                        <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.6rem', textTransform: 'uppercase' }}>{item.l}</Typography>
                        <Typography sx={{ fontWeight: 800, color: item.c, fontSize: '0.95rem' }}>{item.v}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                {/* Client rows */}
                <TableContainer sx={{ maxHeight: 350 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Client', 'Trades', 'Open', 'Turnover', 'Revenue'].map(h => (
                          <TableCell key={h} align={h === 'Client' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB' }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {d.clients.map(c => (
                        <TableRow key={c.clientName} hover>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.85rem', color: COLORS.textPrimary }}>{c.clientName}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{c.tradeCount}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.blue }}>{c.openTrades}</TableCell>
                          <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.textSecondary }}>{formatCurrency(c.turnover)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.positive, fontSize: '0.85rem' }}>{formatCurrency(c.revenue)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            );
          })()
        )}
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5, justifyContent: 'space-between' }}>
        <Button onClick={() => { onClose(); setShowDetails(false); setExpandedDealer(null); }} sx={{ textTransform: 'none', color: COLORS.textSecondary }}>Close</Button>
        <Button onClick={() => { onClose(); setShowDetails(false); setExpandedDealer(null); navigate('/dealer-analytics'); }} sx={{ textTransform: 'none', fontWeight: 700, color: COLORS.primary, fontSize: '0.85rem' }}>View Full Analytics &rarr;</Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// CLIENT BREAKDOWN TABLE (full row, columns: SL, invested, current, pnl, brokerage, realised, unrealised)
// ============================================================================

function ClientBreakdownTable({ trades, clients }: { trades: Trade[]; clients: Client[] }) {
  const stats = useMemo(() => {
    const cMap = new Map(clients.map(c => [c.clientId, c]));
    const m = new Map<string, { name: string; invested: number; current: number; pnl: number; positions: number; brokerage: number; realizedPnL: number; unrealizedPnL: number; openCount: number; closedCount: number }>();
    // Include ALL trades (open + closed) for complete picture
    trades.filter(t => !t.isDeleted).forEach(t => {
      const e = m.get(t.clientId) || { name: t.clientName, invested: 0, current: 0, pnl: 0, positions: 0, brokerage: 0, realizedPnL: 0, unrealizedPnL: 0, openCount: 0, closedCount: 0 };
      e.invested += t.investedAmount || 0;
      e.current += t.currentValue || 0;
      e.pnl += t.totalPnL || 0;
      e.positions += 1;
      e.realizedPnL += t.realizedPnL || 0;
      e.unrealizedPnL += t.unrealizedPnL || 0;
      if (t.status === 'OPEN') e.openCount += 1;
      if (t.status === 'CLOSED') e.closedCount += 1;
      // Lifetime brokerage = every leg ever opened or closed for this client
      // (Bajaj convention). For open positions this includes the buy-side
      // commission already earned even though the trade isn't yet closed.
      const cl = cMap.get(t.clientId);
      if (cl) {
        const c = dailyBrokerageContribution(t, cl.brokerageRates as any);
        e.brokerage += c.buy + c.sell;
      }
      m.set(t.clientId, e);
    });
    return Array.from(m.entries()).map(([id, s]) => ({ clientId: id, ...s })).sort((a, b) => b.invested - a.invested);
  }, [trades, clients]);

  if (stats.length === 0) return <Box sx={{ py: 3, textAlign: 'center' }}><Typography variant="body2" color="text.secondary">No client data</Typography></Box>;

  return (
    <TableContainer sx={{ maxHeight: 300 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {['Client', 'SL', 'Invested', 'Current Price', 'P&L', 'Brokerage', 'Realised P&L', 'Unrealised P&L'].map(h => (
              <TableCell key={h} align={h === 'Client' ? 'left' : 'right'} sx={{ bgcolor: '#F9FAFB', fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 1, borderBottom: `2px solid ${COLORS.border}` }}>{h}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {stats.map(c => (
            <TableRow key={c.clientId} hover>
              <TableCell sx={{ fontWeight: 700, fontSize: '0.8rem', color: COLORS.textPrimary }}>{c.name}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem', color: COLORS.textSecondary }}>—</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(c.invested)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{formatCurrency(c.current)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: c.pnl >= 0 ? COLORS.positive : COLORS.negative, fontSize: '0.8rem' }}>{formatCurrency(c.pnl)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem', color: COLORS.warning }}>{formatCurrency(c.brokerage)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem', color: c.realizedPnL >= 0 ? COLORS.positive : COLORS.negative }}>{formatCurrency(c.realizedPnL)}</TableCell>
              <TableCell align="right" sx={{ fontSize: '0.75rem', color: c.unrealizedPnL >= 0 ? COLORS.positive : COLORS.negative }}>{formatCurrency(c.unrealizedPnL)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ============================================================================
// URGENT CLOSURE TABLE — Trade-wise, 2% of deployed amount threshold
// ============================================================================

// getFuturesSLAmount extracted to utils/stopLoss.ts (shared with RiskDashboard).

interface UrgentPosition {
  key: string;
  clientId: string;
  clientName: string;
  scrip: string;
  tradeTypeLabel: string;   // Intraday | Delivery | Future | Option
  direction: 'BUY' | 'SELL';
  avgPrice: number;          // weighted avg open price
  breakevenRate: number;     // charge-inclusive breakeven
  stopLoss: number;          // SL amount in ₹ (futures: lot-based, others: 2% of deployed)
  technicalStopLoss: number | null; // placeholder for future
  slRate: number;            // price at which SL triggers
  deployedAmount: number;    // frozen per-trade deployed amount
  unrealizedLoss: number;    // charge-inclusive loss (negative)
  lossPercent: number;       // |loss| / deployed × 100
  shares: number;
  lots: number;              // number of lots (for futures display)
  lotSize: number;           // lot size (shares per lot)
  isFuture: boolean;         // true if futures trade
  currentPrice: number;
  isMTF: boolean;
  leverage?: string;
}

function isTradeFromToday(t: Trade): boolean {
  const todayStr = new Date(Date.now() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10); // IST date
  const tradeDate = (t.date || t.createdAt || '').slice(0, 10);
  return tradeDate === todayStr;
}

function getUrgentTradeTypeLabel(tradeType: TradeType, tradeLegs: Trade[]): string {
  if (tradeType === TradeType.FUTURE) return 'Future';
  if (tradeType === TradeType.OPTION) return 'Option';
  // Intraday if any leg was opened today OR is explicitly INTRA_STOCK
  const hasIntra = tradeLegs.some(t => t.tradeType === TradeType.INTRA_STOCK || isTradeFromToday(t));
  return hasIntra ? 'Intraday' : 'Delivery';
}

function selectPositionBreakeven(be: BreakevenResult, tradeType: TradeType, tradeLegs: Trade[]): number {
  if (tradeType === TradeType.FUTURE) return be.futureBE ?? 0;
  if (tradeType === TradeType.OPTION) return be.optionBE ?? 0;
  const hasIntra = tradeLegs.some(t => t.tradeType === TradeType.INTRA_STOCK || isTradeFromToday(t));
  if (hasIntra) return be.intradayBE ?? be.fullClearBE ?? 0;
  return be.carryForwardBE ?? be.deliveryBE ?? be.fullClearBE ?? 0;
}

// Stop Loss info for a position — used by both Open Positions table and Urgent Closure
interface PositionSLInfo {
  beRate: number;          // charge-inclusive breakeven
  slAmount: number;        // SL budget in ₹ (lot-based for futures, 2% deployed for equity/options)
  slRate: number;          // exit price where total loss (incl. exit charges) = slAmount
  chargeInclusivePnL: number; // current loss/profit using BE as cost basis
  slProgressPct: number;   // |loss| / slAmount × 100 (0% = no loss, 100% = SL hit)
  isFuture: boolean;
}

function computePositionSL(p: MergedPosition, clientMap: Map<string, Client>): PositionSLInfo | null {
  const deployed = p.deployedAmount;
  if (!deployed || deployed <= 0) return null;
  if (!p.shares || p.shares <= 0) return null;

  const beRate = selectPositionBreakeven(p.breakeven, p.tradeType, p.tradeLegs);
  if (!beRate || beRate <= 0) return null;

  // Charge-inclusive PnL using breakeven as the true cost basis
  const chargeInclusivePnL = p.direction === 'SELL'
    ? (beRate - p.currentPrice) * p.shares
    : (p.currentPrice - beRate) * p.shares;

  // SL amount: lot-based for futures, 2% of deployed otherwise
  const isFuture = p.tradeType === TradeType.FUTURE;
  const slAmount = isFuture ? getFuturesSLAmount(p.balanceLots) : deployed * 0.02;
  if (!slAmount || slAmount <= 0) return null;

  // Iteratively compute SL rate including exit charges
  const client = clientMap.get(p.clientId);
  const brokerageRates = client?.brokerageRates || { stock: 0.003, option: 100, intraStock: 0.0003 };
  const isIntra = p.tradeLegs.some(t => t.tradeType === TradeType.INTRA_STOCK || isTradeFromToday(t));
  const exitBrokRate = p.tradeType === TradeType.FUTURE
    ? (client?.brokerageRates?.future || 0.0003)
    : isIntra ? (brokerageRates.intraStock || 0.0003) : (brokerageRates.stock || 0.003);
  const exitTradeType = p.tradeType === TradeType.FUTURE ? TradeType.FUTURE
    : p.tradeType === TradeType.OPTION ? TradeType.OPTION
    : isIntra ? TradeType.INTRA_STOCK : TradeType.EQUITY;
  // For options, calculateChargesBreakdown expects the TOTAL fixed brokerage for the whole trade,
  // not the per-lot rate. So multiply the per-lot rate by the number of lots actually being closed.
  const fixedBrokerage = p.tradeType === TradeType.OPTION
    ? (client?.brokerageRates?.option || DEFAULT_OPTION_BROKERAGE) * p.balanceLots
    : undefined;

  let slRate: number;
  if (p.direction === 'BUY') {
    slRate = beRate - slAmount / p.shares;
    for (let i = 0; i < 15; i++) {
      const exitValue = slRate * p.shares;
      const exitCharges = calculateChargesBreakdown(exitTradeType, 0, exitValue, exitBrokRate, isIntra, 'NSE', fixedBrokerage);
      const newSlRate = beRate - (slAmount - exitCharges.totalCharges) / p.shares;
      if (Math.abs(newSlRate - slRate) < 0.001) { slRate = newSlRate; break; }
      slRate = newSlRate;
    }
  } else {
    slRate = beRate + slAmount / p.shares;
    for (let i = 0; i < 15; i++) {
      const exitValue = slRate * p.shares;
      const exitCharges = calculateChargesBreakdown(exitTradeType, exitValue, 0, exitBrokRate, isIntra, 'NSE', fixedBrokerage);
      const newSlRate = beRate + (slAmount - exitCharges.totalCharges) / p.shares;
      if (Math.abs(newSlRate - slRate) < 0.001) { slRate = newSlRate; break; }
      slRate = newSlRate;
    }
  }

  // SL progress: 0 if not yet in loss, 100 = SL hit, >100 = breached
  const slProgressPct = chargeInclusivePnL >= 0 ? 0 : Math.min(999, (Math.abs(chargeInclusivePnL) / slAmount) * 100);

  return {
    beRate: Math.round(beRate * 100) / 100,
    slAmount: Math.round(slAmount),
    slRate: Math.round(slRate * 100) / 100,
    chargeInclusivePnL: Math.round(chargeInclusivePnL),
    slProgressPct: Math.round(slProgressPct * 10) / 10,
    isFuture,
  };
}

function UrgentClosureTable({ positions, clients }: { positions: MergedPosition[]; clients: Client[] }) {
  const urgent = useMemo(() => {
    const clientMap = new Map(clients.map(c => [c.clientId, c]));
    const results: UrgentPosition[] = [];

    for (const p of positions) {
      const deployed = p.deployedAmount;
      if (!deployed || deployed <= 0) continue;

      const sl = computePositionSL(p, clientMap);
      if (!sl) continue;
      if (sl.chargeInclusivePnL >= 0) continue; // no loss
      if (sl.slProgressPct < 100) continue; // SL not yet hit

      const lossAbs = Math.abs(sl.chargeInclusivePnL);
      // lossPercent for display: still |loss| / deployed × 100 (consistent with header)
      const lossPercent = (lossAbs / deployed) * 100;

      results.push({
        key: p.key,
        clientId: p.clientId,
        clientName: p.clientName,
        scrip: p.scrip,
        tradeTypeLabel: getUrgentTradeTypeLabel(p.tradeType, p.tradeLegs),
        direction: p.direction,
        avgPrice: p.weightedAvgBuyPrice,
        breakevenRate: sl.beRate,
        stopLoss: sl.slAmount,
        technicalStopLoss: null,
        slRate: sl.slRate,
        deployedAmount: deployed,
        unrealizedLoss: sl.chargeInclusivePnL,
        lossPercent: Math.round(lossPercent * 100) / 100,
        shares: p.shares,
        lots: p.balanceLots,
        lotSize: p.lotSize,
        isFuture: sl.isFuture,
        currentPrice: p.currentPrice,
        isMTF: p.isMTF,
        leverage: p.leverage,
      });
    }

    return results.sort((a, b) => b.lossPercent - a.lossPercent);
  }, [positions, clients]);

  // Group by scrip for display
  const scripGroups = useMemo(() => {
    const m = new Map<string, UrgentPosition[]>();
    for (const u of urgent) {
      const arr = m.get(u.scrip) || [];
      arr.push(u);
      m.set(u.scrip, arr);
    }
    // Sort groups by worst loss % in the group (most urgent scrip first)
    return Array.from(m.entries())
      .map(([scrip, positions]) => ({
        scrip,
        positions: positions.sort((a, b) => b.lossPercent - a.lossPercent),
        totalLoss: positions.reduce((s, p) => s + p.unrealizedLoss, 0),
        clientCount: positions.length,
        worstLoss: Math.max(...positions.map(p => p.lossPercent)),
      }))
      .sort((a, b) => b.worstLoss - a.worstLoss);
  }, [urgent]);

  if (urgent.length === 0) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><WarningAmberIcon sx={{ fontSize: 28, color: COLORS.textSecondary, mb: 0.5 }} /><Typography variant="body2" color="text.secondary">No urgent closures</Typography></Box>;
  }

  const colHeaders = [
    { l: 'Client', a: 'left' as const },
    { l: 'Type', a: 'left' as const },
    { l: 'Qty', a: 'right' as const },
    { l: 'Avg Price', a: 'right' as const },
    { l: 'BE Rate', a: 'right' as const },
    { l: 'SL', a: 'right' as const },
    { l: 'Tech SL', a: 'right' as const },
    { l: 'SL Rate', a: 'right' as const },
    { l: 'Deployed', a: 'right' as const },
    { l: 'Unreal Loss', a: 'right' as const },
    { l: 'Loss %', a: 'right' as const },
  ];

  return (
    <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
      {scripGroups.map(group => (
        <Accordion
          key={group.scrip}
          defaultExpanded
          disableGutters
          sx={{ '&:before': { display: 'none' }, borderBottom: `1px solid ${COLORS.negative}15`, boxShadow: 'none' }}
        >
          <AccordionSummary
            expandIcon={<Typography sx={{ fontSize: '1rem', color: COLORS.textSecondary }}>&#9662;</Typography>}
            sx={{ px: 1.5, py: 0, minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 }, bgcolor: '#FEF2F2' }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
              <Chip label={group.scrip} size="small" sx={{ fontWeight: 800, fontSize: '0.7rem', bgcolor: `${COLORS.negative}15`, color: COLORS.negativeDark, height: 24 }} />
              <Chip label={`${group.clientCount} client${group.clientCount > 1 ? 's' : ''}`} size="small" sx={{ fontWeight: 600, fontSize: '0.55rem', height: 18, bgcolor: `${COLORS.primary}10`, color: COLORS.primary }} />
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                  Total Loss: <Typography component="span" sx={{ fontWeight: 800, color: COLORS.negative, fontSize: '0.75rem' }}>{formatCurrency(group.totalLoss)}</Typography>
                </Typography>
                <Chip
                  label={`${group.worstLoss.toFixed(1)}%`}
                  size="small"
                  sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: group.worstLoss >= 5 ? COLORS.negative : group.worstLoss >= 3 ? '#F59E0B' : '#FB923C', color: '#fff' }}
                />
              </Box>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {colHeaders.map(h => (
                    <TableCell key={h.l} align={h.a} sx={{ bgcolor: '#FAFAFA', fontWeight: 700, fontSize: '0.55rem', color: COLORS.textSecondary, textTransform: 'uppercase', py: 0.5, px: 1, borderBottom: `1px solid ${COLORS.border}`, whiteSpace: 'nowrap' }}>{h.l}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {group.positions.map(u => (
                  <TableRow key={u.key} sx={{ bgcolor: u.lossPercent >= 5 ? '#FEF2F2' : u.lossPercent >= 3 ? '#FFFBEB' : 'inherit' }}>
                    <TableCell sx={{ px: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{u.clientName}</Typography>
                        {u.isMTF && <Typography component="span" sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7', px: 0.3, py: 0.1, borderRadius: 0.5 }}>MTF{u.leverage ? ` ${u.leverage}` : ''}</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ px: 1 }}>
                      <Box sx={{ display: 'flex', gap: 0.3 }}>
                        <Chip label={u.tradeTypeLabel} size="small" sx={{ fontSize: '0.5rem', height: 16, fontWeight: 600, bgcolor: u.tradeTypeLabel === 'Intraday' ? '#D1FAE5' : u.tradeTypeLabel === 'Future' ? '#DBEAFE' : u.tradeTypeLabel === 'Option' ? '#EDE9FE' : '#E0E7FF', color: u.tradeTypeLabel === 'Intraday' ? '#065F46' : u.tradeTypeLabel === 'Future' ? '#1D4ED8' : u.tradeTypeLabel === 'Option' ? '#6D28D9' : '#3730A3' }} />
                        <Chip label={u.direction} size="small" sx={{ fontSize: '0.45rem', height: 16, fontWeight: 700, bgcolor: u.direction === 'BUY' ? '#D1FAE5' : '#FEE2E2', color: u.direction === 'BUY' ? COLORS.positive : COLORS.negative }} />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ px: 1 }}>
                      {(u.isFuture || u.tradeTypeLabel === 'Option') ? (
                        <Box>
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700 }}>{u.lots} lot{u.lots > 1 ? 's' : ''}</Typography>
                          <Typography sx={{ fontSize: '0.5rem', color: COLORS.textSecondary }}>{u.shares} shares</Typography>
                        </Box>
                      ) : (
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600 }}>{u.shares} sh</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, px: 1 }}>{formatCurrencyFull(u.avgPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: COLORS.primary, px: 1 }}>{formatCurrencyFull(u.breakevenRate)}</TableCell>
                    <TableCell align="right" sx={{ px: 1 }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.warning }}>{formatCurrency(u.stopLoss)}</Typography>
                        <Typography sx={{ fontSize: '0.45rem', color: COLORS.textSecondary }}>
                          {u.isFuture ? `${u.lots} lot SL` : '2% deployed'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, px: 1 }}>—</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 700, color: COLORS.negative, px: 1 }}>{formatCurrencyFull(u.slRate)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, px: 1 }}>{formatCurrency(u.deployedAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.negative, fontSize: '0.8rem', px: 1 }}>{formatCurrency(u.unrealizedLoss)}</TableCell>
                    <TableCell align="right" sx={{ px: 1 }}>
                      <Chip
                        label={`${u.lossPercent.toFixed(2)}%`}
                        size="small"
                        sx={{
                          fontWeight: 700, fontSize: '0.6rem', height: 20,
                          bgcolor: u.lossPercent >= 5 ? COLORS.negative : u.lossPercent >= 3 ? '#F59E0B' : '#FB923C',
                          color: '#fff',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

// ============================================================================
// ULTIMATE CLOSURE — 12% of available cash at start of day (ALL trade types)
// ============================================================================

interface UltimateClosureClient {
  clientId: string;
  clientName: string;
  availableCash: number;         // start-of-day cash (or computed fallback)
  threshold: number;             // 12% of available cash
  totalLoss: number;             // total unrealized loss across ALL positions (negative)
  lossPercent: number;           // |loss| / availableCash × 100
  // Breakdown by trade type
  equityLoss: number;
  equityPositions: number;
  futuresLoss: number;
  futuresPositions: number;
  futuresLots: number;
  optionsLoss: number;
  optionsPositions: number;
  scrips: string[];
}

function UltimateClosureTable({ positions, clients }: { positions: MergedPosition[]; clients: Client[] }) {
  const ultimateClosure = useMemo(() => {
    const clientMap = new Map(clients.map(c => [c.clientId, c]));

    // Group ALL positions by client
    const clientPositions = new Map<string, MergedPosition[]>();
    for (const p of positions) {
      const arr = clientPositions.get(p.clientId) || [];
      arr.push(p);
      clientPositions.set(p.clientId, arr);
    }

    const results: UltimateClosureClient[] = [];

    for (const [clientId, posList] of clientPositions) {
      const client = clientMap.get(clientId);
      if (!client) continue;

      // availableCash = loss-absorption buffer. Prefer start-of-day snapshot;
      // fallback mirrors backend snapshot formula (clients.handler.ts:340-354)
      // so RiskDashboard + TradingDashboard + backend all agree.
      let availableCash = client.startOfDayAvailableCash;
      if (!availableCash || availableCash <= 0) {
        const allRealizedPnL = posList.reduce((s, p) => s + p.realizedPnL, 0);
        const openInvested = posList
          .filter(p => p.balanceLots > 0)
          .reduce((s, p) => s + p.investedAmount, 0);
        availableCash = client.initialCapital + allRealizedPnL - openInvested;
      }
      if (availableCash <= 0) continue;

      // Total unrealized loss across ALL positions for this client
      const totalLoss = posList.reduce((sum, p) => sum + Math.min(p.unrealizedPnL, 0), 0);
      if (totalLoss >= 0) continue; // no loss

      const threshold = availableCash * 0.12;
      const lossAbs = Math.abs(totalLoss);
      const lossPercent = (lossAbs / availableCash) * 100;

      if (lossAbs < threshold) continue; // total loss below 12%

      // Breakdown by type
      const eqPositions = posList.filter(p => isEquityType(p.tradeType));
      const futPositions = posList.filter(p => p.tradeType === TradeType.FUTURE);
      const optPositions = posList.filter(p => p.tradeType === TradeType.OPTION);

      results.push({
        clientId,
        clientName: client.name,
        availableCash,
        threshold: Math.round(threshold),
        totalLoss: Math.round(totalLoss),
        lossPercent: Math.round(lossPercent * 100) / 100,
        equityLoss: Math.round(eqPositions.reduce((s, p) => s + Math.min(p.unrealizedPnL, 0), 0)),
        equityPositions: eqPositions.filter(p => p.unrealizedPnL < 0).length,
        futuresLoss: Math.round(futPositions.reduce((s, p) => s + Math.min(p.unrealizedPnL, 0), 0)),
        futuresPositions: futPositions.filter(p => p.unrealizedPnL < 0).length,
        futuresLots: futPositions.reduce((s, p) => s + p.balanceLots, 0),
        optionsLoss: Math.round(optPositions.reduce((s, p) => s + Math.min(p.unrealizedPnL, 0), 0)),
        optionsPositions: optPositions.filter(p => p.unrealizedPnL < 0).length,
        scrips: posList.filter(p => p.unrealizedPnL < 0).map(p => p.scrip),
      });
    }

    return results.sort((a, b) => b.lossPercent - a.lossPercent);
  }, [positions, clients]);

  if (ultimateClosure.length === 0) return null;

  return (
    <Box sx={{ mx: 1, mb: 1, mt: 1 }}>
      {ultimateClosure.map(uc => (
        <Box
          key={uc.clientId}
          sx={{
            background: '#FEF2F2',
            borderRadius: 2,
            px: 2,
            py: 1.5,
            mb: 0.5,
            border: '1px solid #FCA5A5',
            animation: 'pulseRed 2s infinite',
            '@keyframes pulseRed': {
              '0%, 100%': { boxShadow: '0 0 5px rgba(220, 38, 38, 0.3)' },
              '50%': { boxShadow: '0 0 15px rgba(220, 38, 38, 0.6)' },
            },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            {/* Left: client name + alert */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: '#B91C1C', fontSize: 20 }} />
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontWeight: 600, color: '#0A0A0A', fontSize: '0.85rem' }}>{uc.clientName}</Typography>
                  <Typography sx={{ fontWeight: 600, color: '#B91C1C', fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>ACCOUNT BLOCKED</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.65rem', color: '#737373' }}>
                  12% daily loss limit breached — close all positions for this client, no new trades
                </Typography>
              </Box>
            </Box>

            {/* Right: key numbers */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.55rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Start-of-Day Cash</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(uc.availableCash)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.55rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>12% Limit</Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(uc.threshold)}</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: '0.55rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total Loss</Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#B91C1C', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(uc.totalLoss)}</Typography>
              </Box>
              {/* Type breakdown */}
              {uc.equityLoss < 0 && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.55rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Equity</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(uc.equityLoss)} ({uc.equityPositions})</Typography>
                </Box>
              )}
              {uc.futuresLoss < 0 && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.55rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Futures</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(uc.futuresLoss)} ({uc.futuresLots}L)</Typography>
                </Box>
              )}
              {uc.optionsLoss < 0 && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '0.55rem', color: '#A3A3A3', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Options</Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#0A0A0A', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(uc.optionsLoss)} ({uc.optionsPositions})</Typography>
                </Box>
              )}
              <Chip label={`${uc.lossPercent.toFixed(1)}%`} sx={{ fontWeight: 600, fontSize: '0.7rem', height: 22, bgcolor: '#B91C1C', color: '#FFFFFF', borderRadius: 1 }} />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ============================================================================
// OPEN TRADES POPUP
// ============================================================================
function OpenTradesPopup({ open, onClose, trades }: { open: boolean; onClose: () => void; trades: Trade[] }) {
  const [popupPeriod, setPopupPeriod] = useState<'day' | 'week' | 'month' | 'year' | 'all'>('day');
  const openTrades = useMemo(() => {
    const nonDeleted = trades.filter(t => t.status === 'OPEN' && !t.isDeleted);
    if (popupPeriod === 'all') return nonDeleted;
    const range = getPeriodRange(popupPeriod);
    return nonDeleted.filter(t => isTradeInPeriod(t.date || t.createdAt, range.start, range.end));
  }, [trades, popupPeriod]);
  const byType = useMemo(() => ({
    equity: openTrades.filter(t => isEquityType(t.tradeType)),
    future: openTrades.filter(t => isFutureType(t.tradeType)),
    option: openTrades.filter(t => isOptionType(t.tradeType)),
  }), [openTrades]);
  const totalInvested = openTrades.reduce((s, t) => s + (t.investedAmount || 0), 0);
  const totalCurrent = openTrades.reduce((s, t) => s + (t.currentValue || 0), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, mx: 'auto' } }} sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#667eea', color: '#fff', py: 1.5, px: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>Open Trades Breakdown</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, borderBottom: `1px solid ${COLORS.border}` }}>
          <ToggleButtonGroup value={popupPeriod} exclusive onChange={(_, v) => { if (v) setPopupPeriod(v); }} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, px: 1.5, py: 0.3 } }}>
            <ToggleButton value="day">D</ToggleButton>
            <ToggleButton value="week">W</ToggleButton>
            <ToggleButton value="month">M</ToggleButton>
            <ToggleButton value="year">Y</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: '#F9FAFB', borderBottom: `1px solid ${COLORS.border}` }}>
          {[
            { label: 'Total Open', value: openTrades.length.toString(), color: COLORS.primary },
            { label: 'Equity', value: byType.equity.length.toString(), color: COLORS.positive },
            { label: 'Future', value: byType.future.length.toString(), color: COLORS.blue },
            { label: 'Option', value: byType.option.length.toString(), color: '#8B5CF6' },
            { label: 'Total Invested', value: formatCurrency(totalInvested), color: COLORS.textPrimary },
            { label: 'Current Value', value: formatCurrency(totalCurrent), color: totalCurrent >= totalInvested ? COLORS.positive : COLORS.negative },
          ].map(item => (
            <Box key={item.label} sx={{ flex: 1, p: 1, bgcolor: '#fff', borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, fontSize: '0.55rem', textTransform: 'uppercase' }}>{item.label}</Typography>
              <Typography sx={{ fontWeight: 800, color: item.color, fontSize: '0.95rem', lineHeight: 1.3 }}>{item.value}</Typography>
            </Box>
          ))}
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Client', 'Scrip', 'Type', 'Qty', 'Buy Price', 'CMP', 'Invested', 'Curr Val', 'Unreal P&L', '%'].map(h => (
                  <TableCell key={h} align={h === 'Client' || h === 'Scrip' || h === 'Type' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {openTrades.map(t => {
                const pnlPct = t.investedAmount > 0 ? ((t.unrealizedPnL || 0) / t.investedAmount) * 100 : 0;
                return (
                  <TableRow key={t.tradeId} hover>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{t.clientName}</TableCell>
                    <TableCell><Chip label={t.scrip} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: `${COLORS.primary}10`, color: COLORS.primary, height: 22 }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{t.tradeType === TradeType.OPTION ? 'Opt' : t.tradeType === TradeType.FUTURE ? 'Fut' : 'Eq'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{t.balanceLots}{isOptionType(t.tradeType) ? ` (${t.balanceLots * t.lotSize}sh)` : ''}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrencyFull(t.purchasePrice)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrencyFull(t.currentPrice)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem' }}>{formatCurrency(t.investedAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>{formatCurrency(t.currentValue)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, color: (t.unrealizedPnL || 0) >= 0 ? COLORS.positive : COLORS.negative, fontSize: '0.8rem' }}>{formatCurrency(t.unrealizedPnL)}</TableCell>
                    <TableCell align="right"><Chip label={formatPercent(pnlPct)} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: pnlPct >= 0 ? '#D1FAE5' : '#FEE2E2', color: pnlPct >= 0 ? COLORS.positive : COLORS.negative }} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}><Button onClick={onClose} sx={{ textTransform: 'none', color: COLORS.textSecondary }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ============================================================================
// TURNOVER POPUP
// ============================================================================
function TurnoverPopup({ open, onClose, trades, clients }: { open: boolean; onClose: () => void; trades: Trade[]; clients: Client[] }) {
  const [view, setView] = useState<'client' | 'agent' | 'scrip'>('client');
  const [popupPeriod, setPopupPeriod] = useState<'day' | 'week' | 'month' | 'year' | 'all'>('day');
  const active = useMemo(() => {
    const nonDeleted = trades.filter(t => !t.isDeleted);
    if (popupPeriod === 'all') return nonDeleted;
    const range = getPeriodRange(popupPeriod);
    return nonDeleted.filter(t => {
      // Closed trades bucket by sale date (revenue realisation), open trades by
      // buy date (when capital deployed). Standard across all surfaces.
      const bucketDate = t.status === 'CLOSED' ? (t.saleDate || t.date || t.createdAt) : (t.date || t.createdAt);
      return isTradeInPeriod(bucketDate, range.start, range.end);
    });
  }, [trades, popupPeriod]);
  const cMap = useMemo(() => new Map(clients.map(c => [c.clientId, c])), [clients]);

  // Client-wise
  const clientData = useMemo(() => {
    const m = new Map<string, { name: string; buyTurnover: number; sellTurnover: number; trades: number; scrips: Set<string> }>();
    active.forEach(t => {
      const e = m.get(t.clientId) || { name: t.clientName, buyTurnover: 0, sellTurnover: 0, trades: 0, scrips: new Set<string>() };
      e.buyTurnover += t.purchaseLots * t.lotSize * t.purchasePrice;
      e.sellTurnover += (t.saleLots || 0) * t.lotSize * (t.salePrice || 0);
      e.trades += 1;
      e.scrips.add(t.scrip);
      m.set(t.clientId, e);
    });
    return Array.from(m.values()).sort((a, b) => (b.buyTurnover + b.sellTurnover) - (a.buyTurnover + a.sellTurnover));
  }, [active]);

  // Agent-wise
  const agentData = useMemo(() => {
    const m = new Map<string, { name: string; buyTurnover: number; sellTurnover: number; trades: number; clients: Set<string> }>();
    active.forEach(t => {
      const cl = cMap.get(t.clientId);
      const agentName = cl?.referredByName || 'Unassigned';
      const e = m.get(agentName) || { name: agentName, buyTurnover: 0, sellTurnover: 0, trades: 0, clients: new Set<string>() };
      e.buyTurnover += t.purchaseLots * t.lotSize * t.purchasePrice;
      e.sellTurnover += (t.saleLots || 0) * t.lotSize * (t.salePrice || 0);
      e.trades += 1;
      e.clients.add(t.clientName);
      m.set(agentName, e);
    });
    return Array.from(m.values()).sort((a, b) => (b.buyTurnover + b.sellTurnover) - (a.buyTurnover + a.sellTurnover));
  }, [active, cMap]);

  // Scrip-wise
  const scripData = useMemo(() => {
    const m = new Map<string, { scrip: string; type: string; buyTurnover: number; sellTurnover: number; trades: number }>();
    active.forEach(t => {
      const e = m.get(t.scrip) || { scrip: t.scrip, type: t.tradeType, buyTurnover: 0, sellTurnover: 0, trades: 0 };
      e.buyTurnover += t.purchaseLots * t.lotSize * t.purchasePrice;
      e.sellTurnover += (t.saleLots || 0) * t.lotSize * (t.salePrice || 0);
      e.trades += 1;
      m.set(t.scrip, e);
    });
    return Array.from(m.values()).sort((a, b) => (b.buyTurnover + b.sellTurnover) - (a.buyTurnover + a.sellTurnover));
  }, [active]);

  const totalBuy = active.reduce((s, t) => s + t.purchaseLots * t.lotSize * t.purchasePrice, 0);
  const totalSell = active.reduce((s, t) => s + (t.saleLots || 0) * t.lotSize * (t.salePrice || 0), 0);
  const grandTotal = totalBuy + totalSell;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, mx: 'auto' } }} sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#4facfe', color: '#fff', py: 1.5, px: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>Turnover Breakdown</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {/* Summary cards */}
        <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: '#F9FAFB', borderBottom: `1px solid ${COLORS.border}` }}>
          {[
            { label: 'Total Turnover', value: formatCurrency(grandTotal), color: COLORS.primary },
            { label: 'Buy Turnover', value: formatCurrency(totalBuy), color: COLORS.positive },
            { label: 'Sell Turnover', value: formatCurrency(totalSell), color: COLORS.negative },
            { label: view === 'client' ? 'Clients' : view === 'agent' ? 'Agents' : 'Scrips', value: (view === 'client' ? clientData.length : view === 'agent' ? agentData.length : scripData.length).toString(), color: COLORS.blue },
          ].map(item => (
            <Box key={item.label} sx={{ flex: 1, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, fontSize: '0.6rem', textTransform: 'uppercase' }}>{item.label}</Typography>
              <Typography sx={{ fontWeight: 800, color: item.color, fontSize: '1rem', lineHeight: 1.3 }}>{item.value}</Typography>
            </Box>
          ))}
        </Box>

        {/* Period + View toggles */}
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={popupPeriod}
            exclusive
            onChange={(_, v) => { if (v) setPopupPeriod(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, px: 1.5, py: 0.3 } }}
          >
            <ToggleButton value="day">D</ToggleButton>
            <ToggleButton value="week">W</ToggleButton>
            <ToggleButton value="month">M</ToggleButton>
            <ToggleButton value="year">Y</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={view} exclusive onChange={(_, v) => v && setView(v)} size="small" sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, px: 2, py: 0.3 } }}>
            <ToggleButton value="client">Client</ToggleButton>
            <ToggleButton value="agent">Agent</ToggleButton>
            <ToggleButton value="scrip">Scrip</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {view === 'client' && ['Client', 'Scrips', 'Trades', 'Buy Turnover', 'Sell Turnover', 'Total', '% of Total'].map(h => (
                  <TableCell key={h} align={h === 'Client' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB' }}>{h}</TableCell>
                ))}
                {view === 'agent' && ['Agent', 'Clients', 'Trades', 'Buy Turnover', 'Sell Turnover', 'Total', '% of Total'].map(h => (
                  <TableCell key={h} align={h === 'Agent' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB' }}>{h}</TableCell>
                ))}
                {view === 'scrip' && ['Scrip', 'Type', 'Trades', 'Buy Turnover', 'Sell Turnover', 'Total', '% of Total'].map(h => (
                  <TableCell key={h} align={h === 'Scrip' || h === 'Type' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {view === 'client' && clientData.map(d => {
                const total = d.buyTurnover + d.sellTurnover;
                const pct = grandTotal > 0 ? (total / grandTotal) * 100 : 0;
                return (
                  <TableRow key={d.name} hover>
                    <TableCell><Chip label={d.name} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: `${COLORS.primary}10`, color: COLORS.primary, height: 22 }} /></TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.75rem', color: COLORS.textSecondary }}>{d.scrips.size}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{d.trades}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.positive }}>{formatCurrency(d.buyTurnover)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.negative }}>{formatCurrency(d.sellTurnover)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatCurrency(total)}</TableCell>
                    <TableCell align="right"><Chip label={`${pct.toFixed(1)}%`} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: `${COLORS.blue}15`, color: COLORS.blue }} /></TableCell>
                  </TableRow>
                );
              })}
              {view === 'agent' && agentData.map(d => {
                const total = d.buyTurnover + d.sellTurnover;
                const pct = grandTotal > 0 ? (total / grandTotal) * 100 : 0;
                return (
                  <TableRow key={d.name} hover>
                    <TableCell><Chip label={d.name} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: d.name === 'Unassigned' ? '#F3F4F6' : `${COLORS.primary}10`, color: d.name === 'Unassigned' ? COLORS.textSecondary : COLORS.primary, height: 22 }} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title={Array.from(d.clients).join(', ')} arrow placement="top">
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', cursor: 'help', textDecoration: 'underline dotted' }}>{d.clients.size}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{d.trades}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.positive }}>{formatCurrency(d.buyTurnover)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.negative }}>{formatCurrency(d.sellTurnover)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatCurrency(total)}</TableCell>
                    <TableCell align="right"><Chip label={`${pct.toFixed(1)}%`} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: `${COLORS.blue}15`, color: COLORS.blue }} /></TableCell>
                  </TableRow>
                );
              })}
              {view === 'scrip' && scripData.map(d => {
                const total = d.buyTurnover + d.sellTurnover;
                const pct = grandTotal > 0 ? (total / grandTotal) * 100 : 0;
                return (
                  <TableRow key={d.scrip} hover>
                    <TableCell><Chip label={d.scrip} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: `${COLORS.primary}10`, color: COLORS.primary, height: 22 }} /></TableCell>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{d.type === TradeType.OPTION ? 'Opt' : d.type === TradeType.FUTURE ? 'Fut' : 'Eq'}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{d.trades}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.positive }}>{formatCurrency(d.buyTurnover)}</TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.negative }}>{formatCurrency(d.sellTurnover)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.8rem' }}>{formatCurrency(total)}</TableCell>
                    <TableCell align="right"><Chip label={`${pct.toFixed(1)}%`} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: `${COLORS.blue}15`, color: COLORS.blue }} /></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}><Button onClick={onClose} sx={{ textTransform: 'none', color: COLORS.textSecondary }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ============================================================================
// REALISED P&L POPUP
// ============================================================================
function RealisedPnlPopup({ open, onClose, trades, clients }: { open: boolean; onClose: () => void; trades: Trade[]; clients: Client[] }) {
  const navigate = useNavigate();
  const [popupPeriod, setPopupPeriod] = useState<'day' | 'week' | 'month' | 'year' | 'all'>('day');
  const cMap = useMemo(() => new Map(clients.map(c => [c.clientId, c])), [clients]);
  // Include trades with saleLots > 0 (closed or partially closed)
  const closedTrades = useMemo(() => trades.filter(t => !t.isDeleted && (t.saleLots || 0) > 0), [trades]);
  // Realised P&L popup shows trades CLOSED in the period (saleDay in range).
  const periodFilteredTrades = useMemo(() => {
    if (popupPeriod === 'all') return closedTrades;
    const range = getPeriodRange(popupPeriod);
    return closedTrades.filter(t => {
      const bucketDate = t.saleDate || t.date || t.createdAt;
      return isTradeInPeriod(bucketDate, range.start, range.end);
    });
  }, [closedTrades, popupPeriod]);
  const clientPnL = useMemo(() => {
    const range = popupPeriod === 'all' ? null : getPeriodRange(popupPeriod);
    const startStr = range ? istDay(range.start.toISOString()) : '0000-00-00';
    const endStr = range ? istDay(range.end.toISOString()) : '9999-99-99';
    const m = new Map<string, { clientId: string; name: string; realized: number; brokerage: number; trades: number; winners: number; losers: number }>();
    periodFilteredTrades.forEach(t => {
      const e = m.get(t.clientId) || { clientId: t.clientId, name: t.clientName, realized: 0, brokerage: 0, trades: 0, winners: 0, losers: 0 };
      // Realised P&L stays on close date (uses backend-stored value with full
      // itemized charges deducted). Brokerage attribution is daily Bajaj-style:
      // only the sell-side commission counts here (buy-side was attributed
      // on its own day in other views).
      const realizedPnL = t.realizedPnL || 0;
      const cl = cMap.get(t.clientId);
      let brokerage = 0;
      if (cl) {
        const c = dailyBrokerageContribution(t, cl.brokerageRates as any);
        if (c.sellDay && c.sellDay >= startStr && c.sellDay <= endStr) brokerage += c.sell;
        // Intraday trades have full commission on c.sell already (helper places it there).
        // Buy-side commission for delivery is intentionally excluded from this popup.
      }
      e.realized += realizedPnL;
      e.brokerage += brokerage;
      e.trades += 1;
      // Break-even (rPnL=0) counts as a winner (it's not a loss).
      if (realizedPnL >= 0) e.winners += 1; else e.losers += 1;
      m.set(t.clientId, e);
    });
    return Array.from(m.values()).sort((a, b) => b.realized - a.realized);
  }, [periodFilteredTrades, cMap]);
  const totalRealized = clientPnL.reduce((s, c) => s + c.realized, 0);
  const totalBrokerage = clientPnL.reduce((s, c) => s + c.brokerage, 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, mx: 'auto' } }} sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: totalRealized >= 0 ? '#11998e' : '#eb3349', color: '#fff', py: 1.5, px: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff' }}>Realised P&L Breakdown</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, pt: 1.5, pb: 0.5, borderBottom: `1px solid ${COLORS.border}` }}>
          <ToggleButtonGroup
            value={popupPeriod}
            exclusive
            onChange={(_, v) => { if (v) setPopupPeriod(v); }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.7rem', fontWeight: 600, px: 1.5, py: 0.3 } }}
          >
            <ToggleButton value="day">D</ToggleButton>
            <ToggleButton value="week">W</ToggleButton>
            <ToggleButton value="month">M</ToggleButton>
            <ToggleButton value="year">Y</ToggleButton>
            <ToggleButton value="all">All</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, p: 2, bgcolor: '#F9FAFB', borderBottom: `1px solid ${COLORS.border}` }}>
          {[
            { label: 'Total Realised', value: formatCurrency(totalRealized), color: totalRealized >= 0 ? COLORS.positive : COLORS.negative },
            { label: 'Brokerage Paid', value: formatCurrency(totalBrokerage), color: COLORS.warning },
            { label: 'Net After Brokerage', value: formatCurrency(totalRealized), color: totalRealized >= 0 ? COLORS.positive : COLORS.negative },
            { label: 'Closed Trades', value: periodFilteredTrades.length.toString(), color: COLORS.primary },
          ].map(item => (
            <Box key={item.label} sx={{ flex: 1, p: 1.5, bgcolor: '#fff', borderRadius: 2, border: `1px solid ${COLORS.border}` }}>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, fontSize: '0.6rem', textTransform: 'uppercase' }}>{item.label}</Typography>
              <Typography sx={{ fontWeight: 800, color: item.color, fontSize: '1rem', lineHeight: 1.3 }}>{item.value}</Typography>
            </Box>
          ))}
        </Box>
        <TableContainer sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['Client', 'Closed Trades', 'Winners', 'Losers', 'Win Rate', 'Brokerage', 'Realised P&L'].map(h => (
                  <TableCell key={h} align={h === 'Client' ? 'left' : 'right'} sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {clientPnL.map(c => {
                const winRate = c.trades > 0 ? (c.winners / c.trades) * 100 : 0;
                return (
                  <TableRow key={c.name} hover sx={{ cursor: 'pointer' }} onClick={() => {
                    onClose();
                    navigate(`/trades?status=CLOSED&clientId=${c.clientId}`);
                  }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem', color: COLORS.textPrimary }}>{c.name}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{c.trades}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: COLORS.positive, fontSize: '0.8rem' }}>{c.winners}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: COLORS.negative, fontSize: '0.8rem' }}>{c.losers}</TableCell>
                    <TableCell align="right"><Chip label={`${winRate.toFixed(0)}%`} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: winRate >= 50 ? '#D1FAE5' : '#FEE2E2', color: winRate >= 50 ? COLORS.positive : COLORS.negative }} /></TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.8rem', color: COLORS.warning }}>{formatCurrency(c.brokerage)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: c.realized >= 0 ? COLORS.positive : COLORS.negative, fontSize: '0.9rem' }}>{formatCurrency(c.realized)}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ bgcolor: '#F0F0FF' }}>
                <TableCell sx={{ fontWeight: 800, fontSize: '0.85rem' }}>Total</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>{periodFilteredTrades.length}</TableCell>
                <TableCell colSpan={3} />
                <TableCell align="right" sx={{ fontWeight: 700, color: COLORS.warning }}>{formatCurrency(totalBrokerage)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 800, color: totalRealized >= 0 ? COLORS.positive : COLORS.negative, fontSize: '0.9rem' }}>{formatCurrency(totalRealized)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 1.5 }}><Button onClick={onClose} sx={{ textTransform: 'none', color: COLORS.textSecondary }}>Close</Button></DialogActions>
    </Dialog>
  );
}

// ============================================================================
// BREAKEVEN POPUP
// ============================================================================

function BECell({ beValue, pnl, cmp }: { beValue: number | null | undefined; pnl: number | null; cmp: number }) {
  if (!beValue) return <TableCell align="right" sx={{ px: 1 }}><Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>—</Typography></TableCell>;
  return (
    <TableCell align="right" sx={{ px: 1 }}>
      <Box>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: cmp >= beValue ? COLORS.positive : COLORS.negative }}>
          {formatCurrencyFull(beValue)}
        </Typography>
        {pnl !== null && (
          <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: pnl >= 0 ? COLORS.positive : COLORS.negative }}>
            {pnl >= 0 ? '+' : ''}{formatCurrency(pnl)}
          </Typography>
        )}
      </Box>
    </TableCell>
  );
}

function BreakevenDetailTable({ positions, labelColumn }: { positions: MergedPosition[]; labelColumn: 'client' | 'scrip' }) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#F9FAFB', textTransform: 'uppercase', py: 0.8 }}>{labelColumn === 'client' ? 'Scrip' : 'Client'}</TableCell>
            <TableCell sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#F9FAFB', textTransform: 'uppercase', py: 0.8 }}>Type</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#F9FAFB', textTransform: 'uppercase', py: 0.8 }}>Qty</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#F9FAFB', textTransform: 'uppercase', py: 0.8 }}>Avg Price</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#F9FAFB', textTransform: 'uppercase', py: 0.8 }}>CMP</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: '#FAFAFA', textTransform: 'uppercase', color: '#0A0A0A', py: 0.8, letterSpacing: '0.04em' }}>Intra BE</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#EFF6FF', textTransform: 'uppercase', color: '#3B82F6', py: 0.8 }}>Del BE</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#FEF2F2', textTransform: 'uppercase', color: '#DC2626', py: 0.8 }}>Clear BE</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#FFF7ED', textTransform: 'uppercase', color: '#D97706', py: 0.8 }}>CF BE</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map(p => {
            const eq = isEquityType(p.tradeType);
            const be = p.breakeven;
            const cmp = p.currentPrice;
            const qty = p.shares;
            const sf = p.direction === 'SELL';
            const pnlSign = sf ? -1 : 1; // sell-first: profit when price below BE
            const intraPnL = be.intradayBE ? pnlSign * (cmp - be.intradayBE) * qty : null;
            const delPnL = be.deliveryBE ? pnlSign * (cmp - be.deliveryBE) * qty : null;
            const clearPnL = be.fullClearBE ? pnlSign * (cmp - be.fullClearBE) * qty : null;
            const cfPnL = be.carryForwardBE ? pnlSign * (cmp - be.carryForwardBE) * qty : null;
            const foBE = be.futureBE || be.optionBE || null;
            const foPnL = foBE ? pnlSign * (cmp - foBE) * qty : null;
            const dash = <TableCell align="right" sx={{ px: 1 }}><Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>—</Typography></TableCell>;

            return (
              <TableRow key={p.key} hover>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                  {labelColumn === 'client' ? (
                    <Chip label={p.scrip} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: '#0A0A0A10', color: '#0A0A0A', height: 22 }} />
                  ) : p.clientName}
                </TableCell>
                <TableCell sx={{ fontSize: '0.7rem' }}>{eq ? 'Eq' : p.tradeType === TradeType.FUTURE ? 'Fut' : 'Opt'}{p.isMTF ? <Typography component="span" sx={{ fontSize: '0.5rem', fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7', px: 0.3, py: 0.1, borderRadius: 0.5, ml: 0.3 }}>MTF{p.leverage ? ` ${p.leverage}` : ''}</Typography> : null}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{qty}</TableCell>
                <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrencyFull(p.weightedAvgBuyPrice)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{formatCurrencyFull(cmp)}</TableCell>
                {/* Intra BE (equity) or single F&O BE */}
                <BECell beValue={eq ? be.intradayBE : foBE} pnl={eq ? intraPnL : foPnL} cmp={cmp} />
                {/* Del BE */}
                {eq ? <BECell beValue={be.deliveryBE} pnl={delPnL} cmp={cmp} /> : dash}
                {/* Clear BE */}
                {eq ? <BECell beValue={be.fullClearBE} pnl={clearPnL} cmp={cmp} /> : dash}
                {/* Carry Forward BE */}
                {eq ? <BECell beValue={be.carryForwardBE} pnl={cfPnL} cmp={cmp} /> : dash}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BreakevenPopup({ open, onClose, positions }: { open: boolean; onClose: () => void; positions: MergedPosition[] }) {
  const [viewMode, setViewMode] = useState<'client' | 'scrip'>('client');
  // Accordions always open (defaultExpanded)

  const clientGroups = useMemo(() => {
    const m = new Map<string, MergedPosition[]>();
    positions.forEach(p => {
      const arr = m.get(p.clientName) || [];
      arr.push(p);
      m.set(p.clientName, arr);
    });
    return Array.from(m.entries()).map(([name, items]) => ({
      name,
      positions: items,
      count: items.length,
      totalInvested: items.reduce((s, p) => s + p.investedAmount, 0),
    })).sort((a, b) => b.totalInvested - a.totalInvested);
  }, [positions]);

  const scripGroups = useMemo(() => {
    const m = new Map<string, MergedPosition[]>();
    positions.forEach(p => {
      const arr = m.get(p.scrip) || [];
      arr.push(p);
      m.set(p.scrip, arr);
    });
    return Array.from(m.entries()).map(([name, items]) => ({
      name,
      positions: items,
      count: items.length,
      totalInvested: items.reduce((s, p) => s + p.investedAmount, 0),
    })).sort((a, b) => b.totalInvested - a.totalInvested);
  }, [positions]);

  const groups = viewMode === 'client' ? clientGroups : scripGroups;

  return (
    <Dialog open={open} onClose={() => { onClose(); /* noop */; }} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, mx: 'auto' } }} sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}>
      <DialogTitle sx={{ bgcolor: '#0A0A0A', color: '#FFFFFF', display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, px: 2.5 }}>
        <Typography variant="h6" fontWeight={700} color="#fff">Breakeven Analysis</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, v) => { if (v) { setViewMode(v); /* noop */; } }}
            size="small"
            sx={{ '& .MuiToggleButton-root': { color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.3)', px: 1.5, py: 0.3, fontSize: '0.7rem', fontWeight: 700, textTransform: 'none', '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.25)', color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' } } } }}
          >
            <ToggleButton value="client">Client Wise</ToggleButton>
            <ToggleButton value="scrip">Scrip Wise</ToggleButton>
          </ToggleButtonGroup>
          <IconButton size="small" onClick={() => { onClose(); /* noop */; }} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
          {groups.map(g => (
            <Accordion
              key={g.name}
              defaultExpanded
              disableGutters
              sx={{ '&:before': { display: 'none' }, borderBottom: `1px solid ${COLORS.border}` }}
            >
              <AccordionSummary
                expandIcon={<Typography sx={{ fontSize: '1rem', color: COLORS.textSecondary }}>&#9662;</Typography>}
                sx={{ px: 2, py: 0.5, '&:hover': { bgcolor: `${COLORS.primary}05` } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: COLORS.textPrimary, minWidth: 120 }}>{g.name}</Typography>
                  <Chip label={`${g.count} positions`} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', height: 20, bgcolor: `${COLORS.primary}12`, color: COLORS.primary }} />
                  <Typography sx={{ fontSize: '0.75rem', color: COLORS.textSecondary, ml: 'auto' }}>
                    Invested: <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.75rem', color: COLORS.textPrimary }}>{formatCurrency(g.totalInvested)}</Typography>
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <BreakevenDetailTable positions={g.positions} labelColumn={viewMode === 'client' ? 'client' : 'scrip'} />
              </AccordionDetails>
            </Accordion>
          ))}
          {groups.length === 0 && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">No open positions</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={() => { onClose(); /* noop */; }} sx={{ textTransform: 'none', color: '#6B7280' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

export default function TradingDashboard() {
  const navigate = useNavigate();
  const [allTrades, setAllTrades] = useState<Trade[]>([]);
  const [dashboardData, setDashboardData] = useState<DashboardSummary | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [clientFilter, setClientFilter] = useState('all');
  const [scripFilter, setScripFilter] = useState('');
  const [periodView, setPeriodView] = useState<PeriodView>('day');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });
  const [searchParams, setSearchParams] = useSearchParams();
  const [showTradeForm, setShowTradeForm] = useState(() => searchParams.get('newTrade') === 'true');
  const [editingTrade, setEditingTrade] = useState<Trade | null>(null);
  const [prefillData, setPrefillData] = useState<{ clientId: string; scrip: string; tradeType: string; direction?: 'BUY' | 'SELL' } | null>(null);
  const [dealerPopupOpen, setDealerPopupOpen] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [commissionOpen, setCommissionOpen] = useState(false);
  const [priceUpdateTime, setPriceUpdateTime] = useState<string | null>(null);
  const [priceSource, setPriceSource] = useState<string>(''); // 'kite' or 'yahoo'
  const [openTradesPopup, setOpenTradesPopup] = useState(false);
  const [turnoverPopup, setTurnoverPopup] = useState(false);
  const [realisedPnlPopup, setRealisedPnlPopup] = useState(false);
  const [breakevenPopup, setBreakevenPopup] = useState(false);
  const [positionDetail, setPositionDetail] = useState<MergedPosition | null>(null);
  const [currentBreakeven, setCurrentBreakeven] = useState<BreakevenResult | undefined>(undefined);
  const [lifoSellForm, setLifoSellForm] = useState<{ show: boolean; qty: number | ''; price: number | ''; loading: boolean; error: string | null }>({ show: false, qty: '', price: '', loading: false, error: null });
  // Quick-entry: TradingView-style keystroke-to-open. When the user presses a
  // printable key on the dashboard (and nothing else is focused / no modifier
  // is held), open the New Trade dialog with that character pre-typed into the
  // client search field.
  const [quickEntryQuery, setQuickEntryQuery] = useState<string>('');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true); setError('');
      const [t, s, c, empData] = await Promise.all([tradeService.getTrades(), analyticsService.getDashboardSummary(), clientService.getClients(), salaryService.getEmployees().catch(() => ({ employees: [] }))]);
      const tradesData = t;
      setAllTrades(tradesData); setDashboardData(s); setClients(c); setEmployees(empData.employees || []);

      // Snapshot start-of-day available cash (runs once per day, skips if already done)
      clientService.snapshotDailyCash().then(res => {
        // Re-fetch clients to get updated startOfDayAvailableCash
        if (res.message.includes('0 clients')) return; // nothing changed
        clientService.getClients().then(updated => setClients(updated)).catch(() => {});
      }).catch(() => {}); // non-fatal

      // Fetch live prices for open positions
      const openScrips = [...new Set(tradesData.filter(t => t.status === 'OPEN' && !t.isDeleted).map(t => t.scrip).filter(Boolean))];
      if (openScrips.length > 0) {
        try {
          const { prices, source } = await marketService.getQuotes(openScrips);
          if (source) setPriceSource(source);
          const updatedTrades = tradesData.map(t => {
            if (t.status === 'OPEN' && prices[t.scrip]) {
              const livePrice = prices[t.scrip].price;
              if (!livePrice || livePrice <= 0) return t;
              const balanceLots = t.purchaseLots - (t.saleLots || 0);
              const investedAmount = balanceLots * t.lotSize * t.purchasePrice;
              const currentValue = livePrice * balanceLots * t.lotSize;
              const isSellFirst = t.direction === 'SELL';
              const unrealizedPnL = isSellFirst ? (investedAmount - currentValue) : (currentValue - investedAmount);
              return { ...t, currentPrice: livePrice, currentValue, unrealizedPnL, totalPnL: (t.realizedPnL || 0) + unrealizedPnL };
            }
            return t;
          });
          setAllTrades(updatedTrades);
          setPriceUpdateTime(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }));
        } catch (e) {
          console.warn('Failed to fetch live prices:', e);
          // Non-fatal - dashboard still works with stored prices
        }
      }
    } catch (err: any) { setError(err?.message || 'Failed to load data'); } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open trade dialog if redirected from /trades/new
  useEffect(() => {
    if (searchParams.get('newTrade') === 'true') {
      setShowTradeForm(true);
      searchParams.delete('newTrade');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // TradingView-style quick entry: any printable letter/digit pressed on the
  // dashboard (when no input/modal is focused and no modifier key is held)
  // opens the New Trade dialog with that character seeding the client search.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (showTradeForm) return; // already open — let the dialog handle its own keys
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key.length !== 1) return; // ignore non-printable keys (Shift, Enter, Arrow, etc.)
      if (!/^[a-zA-Z0-9]$/.test(e.key)) return; // letters and digits only
      const target = e.target as HTMLElement | null;
      // Skip if the user is typing into any form control or contenteditable
      if (target) {
        const tag = target.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (target.isContentEditable) return;
        // Defensive: MUI wraps some inputs; check role
        if (target.getAttribute && target.getAttribute('role') === 'textbox') return;
      }
      e.preventDefault();
      setEditingTrade(null);
      setPrefillData(null);
      setCurrentBreakeven(undefined);
      setQuickEntryQuery(e.key);
      setShowTradeForm(true);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showTradeForm]);

  // Clear the seed once the dialog closes so the next plain click opens blank
  useEffect(() => {
    if (!showTradeForm && quickEntryQuery) setQuickEntryQuery('');
  }, [showTradeForm, quickEntryQuery]);

  // Lightweight price-only refresh every 2 seconds during market hours
  const refreshPricesOnly = useCallback(async () => {
    const openScrips = [...new Set(allTrades.filter(t => t.status === 'OPEN' && !t.isDeleted).map(t => t.scrip).filter(Boolean))];
    if (openScrips.length === 0) return;
    try {
      const { prices, source } = await marketService.getQuotes(openScrips);
      if (source) setPriceSource(source);
      setAllTrades(prev => prev.map(t => {
        if (t.status === 'OPEN' && prices[t.scrip]) {
          const livePrice = prices[t.scrip].price;
          if (!livePrice || livePrice <= 0) return t;
          const balanceLots = t.purchaseLots - (t.saleLots || 0);
          const investedAmount = balanceLots * t.lotSize * t.purchasePrice;
          const currentValue = livePrice * balanceLots * t.lotSize;
          const isSellFirst = t.direction === 'SELL';
          const unrealizedPnL = isSellFirst ? (investedAmount - currentValue) : (currentValue - investedAmount);
          return { ...t, currentPrice: livePrice, currentValue, unrealizedPnL, totalPnL: (t.realizedPnL || 0) + unrealizedPnL };
        }
        return t;
      }));
      setPriceUpdateTime(new Date().toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch { /* non-fatal */ }
  }, [allTrades]);

  useEffect(() => {
    const interval = setInterval(() => {
      const now = new Date();
      const istHour = (now.getUTCHours() + 5) % 24 + (now.getUTCMinutes() + 30 >= 60 ? 1 : 0);
      const istMin = (now.getUTCMinutes() + 30) % 60;
      const day = now.getUTCDay();
      if (day >= 1 && day <= 5 && (istHour > 9 || (istHour === 9 && istMin >= 0)) && (istHour < 15 || (istHour === 15 && istMin <= 35))) {
        refreshPricesOnly();
      }
    }, 2 * 1000); // 2 seconds — prices only, not full data reload
    return () => clearInterval(interval);
  }, [refreshPricesOnly]);

  const trades = allTrades;
  const dashboard = dashboardData;

  // All merged open positions (unfiltered) — used for urgent closure (must not be hidden by client/scrip filter)
  const allMergedPositions = useMemo(() => mergeOpenPositions(trades, clients), [trades, clients]);

  // Pre-compute Stop Loss info for every position (used by table + urgent closure)
  const slInfoMap = useMemo(() => {
    const cMap = new Map(clients.map(c => [c.clientId, c]));
    const m = new Map<string, PositionSLInfo>();
    for (const p of allMergedPositions) {
      const info = computePositionSL(p, cMap);
      if (info) m.set(p.key, info);
    }
    return m;
  }, [allMergedPositions, clients]);

  // Computed - merged open positions (filtered for table display)
  const mergedPositions = useMemo(() => {
    let positions = allMergedPositions;
    if (clientFilter !== 'all') positions = positions.filter(p => p.clientId === clientFilter);
    if (scripFilter) positions = positions.filter(p => p.scrip?.toLowerCase().includes(scripFilter.toLowerCase()));
    return positions;
  }, [allMergedPositions, clientFilter, scripFilter]);

  const sortedPositions = useMemo(() => {
    const s = [...mergedPositions];
    s.sort((a, b) => { const av = (a as any)[sortConfig.key] ?? 0; const bv = (b as any)[sortConfig.key] ?? 0; if (typeof av === 'string') return sortConfig.direction === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av); return sortConfig.direction === 'asc' ? av - bv : bv - av; });
    return s;
  }, [mergedPositions, sortConfig]);

  const allTotals = useMemo(() => {
    const a = trades.filter(t => !t.isDeleted);
    return { total: a.length, open: a.filter(t => t.status === 'OPEN').length, closed: a.filter(t => t.status === 'CLOSED').length };
  }, [trades]);

  const periodAnalytics = useMemo(() => {
    const range = getPeriodRange(periodView);
    const startStr = istDay(range.start.toISOString());
    const endStr = istDay(range.end.toISOString());
    // Include any trade whose buy day OR sell day falls in the period
    // (Bajaj-style daily attribution can split a single trade across two days).
    const pt = trades.filter(t => {
      if (t.isDeleted) return false;
      const buyIn = isTradeInPeriod(t.date || t.createdAt || '', range.start, range.end);
      const sellIn = (t.saleLots || 0) > 0 &&
        isTradeInPeriod(t.saleDate || t.date || t.createdAt || '', range.start, range.end);
      return buyIn || sellIn;
    });
    return {
      label: range.label,
      totalTrades: pt.length,
      brokerage: (() => {
        const cMap = new Map(clients.map(c => [c.clientId, c]));
        return pt.reduce((s, t) => {
          const cl = cMap.get(t.clientId);
          if (!cl) return s;
          const c = dailyBrokerageContribution(t, cl.brokerageRates as any);
          let v = 0;
          if (c.buyDay >= startStr && c.buyDay <= endStr) v += c.buy;
          if (c.sellDay && c.sellDay >= startStr && c.sellDay <= endStr) v += c.sell;
          return s + v;
        }, 0);
      })(),
      // Use backend-stored realizedPnL (full itemized charges already deducted:
      // base brokerage + STT + SEBI + exchange + stamp + 18% GST). Gross
      // recomputation would ignore statutory charges and overstate profit.
      realizedPnL: pt.filter(t => (t.saleLots || 0) > 0).reduce((s, t) => s + (t.realizedPnL || 0), 0),
      // Turnover includes ALL trade types (equity + intra + future + option).
      // The Turnover Breakdown popup, Analytics page, DealerAnalytics, and
      // CommissionCalculator all include options in turnover — the KPI must
      // match or clicking it shows a larger number than the card.
      turnover: pt.reduce((s, t) => s + (t.purchaseLots * t.lotSize * t.purchasePrice) + ((t.saleLots || 0) * t.lotSize * (t.salePrice || 0)), 0),
      optionLots: pt.filter(t => isOptionType(t.tradeType)).reduce((s, t) => s + t.purchaseLots + (t.saleLots || 0), 0),
      eq: pt.filter(t => isEquityType(t.tradeType)).length,
      fut: pt.filter(t => isFutureType(t.tradeType)).length,
      opt: pt.filter(t => isOptionType(t.tradeType)).length,
      open: pt.filter(t => t.status === 'OPEN').length,
      closed: pt.filter(t => t.status === 'CLOSED').length,
      eqOpen: pt.filter(t => t.status === 'OPEN' && isEquityType(t.tradeType)).length,
      futOpen: pt.filter(t => t.status === 'OPEN' && isFutureType(t.tradeType)).length,
      optOpen: pt.filter(t => t.status === 'OPEN' && isOptionType(t.tradeType)).length,
    };
  }, [trades, clients, periodView]);

  const activeClients = useMemo(() => clients.filter(c => c.isActive), [clients]);
  const handleSort = (key: string) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'desc' ? 'asc' : 'desc' }));
  // pnlPct removed - summary bar removed from open positions

  if (loading) {
    return (
      <Box sx={{ p: 2, bgcolor: COLORS.pageBg, minHeight: '100vh' }}>
        <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />
        <Grid container spacing={1.5} sx={{ mb: 2 }}>{[1, 2, 3, 4].map(i => <Grid item xs={6} md={3} key={i}><Skeleton variant="rounded" height={90} sx={{ borderRadius: 3 }} /></Grid>)}</Grid>
        <Skeleton variant="rounded" height={350} sx={{ borderRadius: 3, mb: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: COLORS.pageBg, minHeight: '100vh', pb: 3 }}>
      {/* TOP BAR */}
      <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 1.5, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ color: COLORS.textSecondary, fontWeight: 600, mr: 0.5 }}>{new Date().toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</Typography>

          {/* Clickable stat pill — Clients */}
          <Tooltip title="View all clients" arrow>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => navigate('/clients')}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/clients'); }}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: 0.5,
                px: 1, py: 0.35,
                borderRadius: 999,
                border: `1px solid ${COLORS.border}`,
                bgcolor: '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: COLORS.primary, bgcolor: '#FDF2F8', '& .stat-num': { color: COLORS.primary } },
                '&:active': { transform: 'translateY(1px)' },
              }}
            >
              <PeopleIcon sx={{ fontSize: 13, color: COLORS.textSecondary }} />
              <Typography variant="caption" className="stat-num" sx={{ color: COLORS.textPrimary, fontWeight: 700, transition: 'color 0.15s ease' }}>{dashboard?.activeClients || 0}</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>clients</Typography>
            </Box>
          </Tooltip>

          {/* Clickable stat pill — Open / Closed */}
          <Box
            sx={{
              display: 'inline-flex', alignItems: 'stretch',
              borderRadius: 999,
              border: `1px solid ${COLORS.border}`,
              bgcolor: '#FFFFFF',
              overflow: 'hidden',
            }}
          >
            <Tooltip title="View open trades" arrow>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => navigate('/trades?status=OPEN')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/trades?status=OPEN'); }}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.4,
                  px: 1, py: 0.35,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: '#ECFEFF', '& .stat-num': { color: '#0891B2' } },
                  '&:active': { transform: 'translateY(1px)' },
                }}
              >
                <InventoryIcon sx={{ fontSize: 13, color: COLORS.textSecondary }} />
                <Typography variant="caption" className="stat-num" sx={{ color: COLORS.textPrimary, fontWeight: 700, transition: 'color 0.15s ease' }}>{allTotals.open}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>open</Typography>
              </Box>
            </Tooltip>
            <Box sx={{ width: '1px', bgcolor: COLORS.border }} />
            <Tooltip title="View closed trades (today)" arrow>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => navigate('/trades?status=CLOSED&period=day')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate('/trades?status=CLOSED&period=day'); }}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.4,
                  px: 1, py: 0.35,
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease',
                  '&:hover': { bgcolor: '#F0FDF4', '& .stat-num': { color: '#059669' } },
                  '&:active': { transform: 'translateY(1px)' },
                }}
              >
                <Typography variant="caption" className="stat-num" sx={{ color: COLORS.textPrimary, fontWeight: 700, transition: 'color 0.15s ease' }}>{allTotals.closed}</Typography>
                <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 500 }}>closed</Typography>
              </Box>
            </Tooltip>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Commission Calculator"><IconButton size="small" onClick={() => setCommissionOpen(true)} sx={{ color: COLORS.primary }}><CalculateIcon fontSize="small" /></IconButton></Tooltip>
          {priceUpdateTime && (
            <Tooltip title={priceSource === 'kite'
              ? `Last update: ${priceUpdateTime} | Source: Zerodha Kite (real-time via WebSocket)`
              : `Last update: ${priceUpdateTime} | Source: Yahoo Finance (delayed)\n\nLive prices available Mon–Fri 9:00 AM – 3:30 PM IST via Zerodha Kite.\nOutside market hours, prices are fetched from Yahoo Finance.`
            }>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mr: 0.5 }}>
                <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: priceSource === 'kite' ? '#10B981' : '#F59E0B', animation: priceSource === 'kite' ? 'pulse 2s infinite' : 'none', '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
                <Typography variant="caption" sx={{ color: priceSource === 'kite' ? '#10B981' : '#F59E0B', fontWeight: 600, fontSize: '0.65rem' }}>
                  {priceSource === 'kite' ? 'Live' : 'Delayed'}: {priceUpdateTime}
                </Typography>
              </Box>
            </Tooltip>
          )}
          <Tooltip title="Import Trades from Excel"><IconButton size="small" onClick={() => navigate('/import')} sx={{ color: COLORS.primary }}><UploadFileIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="Refresh"><IconButton size="small" onClick={fetchData} sx={{ color: COLORS.primary }}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
          <Tooltip title="New Trade"><IconButton size="small" onClick={() => { setEditingTrade(null); setPrefillData(null); setCurrentBreakeven(undefined); setShowTradeForm(true); }} sx={{ color: '#fff', bgcolor: COLORS.primary, '&:hover': { bgcolor: COLORS.primaryDark } }}><AddIcon fontSize="small" /></IconButton></Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mx: 2.5, mb: 1, borderRadius: 2 }}>{error}</Alert>}

      <Box sx={{ px: { xs: 1.5, md: 2.5 } }}>
        {/* REVENUE & ACTIVITY - period-based stat cards with D/W/M/Y */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}`, overflow: 'hidden', mb: 2 }}>
          <Box sx={{ p: 1, px: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.cardBg }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AssessmentIcon sx={{ color: COLORS.secondary, fontSize: 18 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: COLORS.textPrimary }}>Revenue & Activity</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontWeight: 600, fontSize: '0.65rem' }}>{periodAnalytics.label}</Typography>
            </Box>
            <ToggleButtonGroup value={periodView} exclusive onChange={(_, v) => v && setPeriodView(v)} size="small"
              sx={{ '& .MuiToggleButton-root': { borderRadius: '6px !important', px: 1.5, py: 0.2, fontWeight: 600, fontSize: '0.65rem', textTransform: 'none', border: `1px solid ${COLORS.border}`, '&.Mui-selected': { bgcolor: COLORS.primary, color: '#fff', '&:hover': { bgcolor: COLORS.primaryDark } } } }}>
              <ToggleButton value="day"><TodayIcon sx={{ fontSize: 12, mr: 0.3 }} />D</ToggleButton>
              <ToggleButton value="week"><ViewWeekIcon sx={{ fontSize: 12, mr: 0.3 }} />W</ToggleButton>
              <ToggleButton value="month"><DateRangeIcon sx={{ fontSize: 12, mr: 0.3 }} />M</ToggleButton>
              <ToggleButton value="year"><CalendarMonthIcon sx={{ fontSize: 12, mr: 0.3 }} />Y</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          <Grid container spacing={1.5} sx={{ p: 1.5 }}>
            <Grid item xs={6} md={4} lg={2.4}>
              <Paper elevation={0} onClick={() => setOpenTradesPopup(true)} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E5E5', height: '100%', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: '#A3A3A3' } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#737373', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>Open Trades</Typography>
                    <Typography sx={{ color: '#0A0A0A', fontWeight: 600, mt: 0.25, lineHeight: 1.1, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>{periodAnalytics.open}</Typography>
                    <Box sx={{ display: 'flex', gap: 0.4, mt: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={`E ${periodAnalytics.eqOpen}`} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 500, bgcolor: 'transparent', color: '#525252', border: '1px solid #E5E5E5', '& .MuiChip-label': { px: 0.6 } }} />
                      <Chip label={`F ${periodAnalytics.futOpen}`} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 500, bgcolor: 'transparent', color: '#525252', border: '1px solid #E5E5E5', '& .MuiChip-label': { px: 0.6 } }} />
                      <Chip label={`O ${periodAnalytics.optOpen}`} size="small" sx={{ height: 16, fontSize: '0.58rem', fontWeight: 500, bgcolor: 'transparent', color: '#525252', border: '1px solid #E5E5E5', '& .MuiChip-label': { px: 0.6 } }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: '#A3A3A3', mt: 0.3, display: 'block', fontSize: '0.62rem' }}>{periodAnalytics.totalTrades} total trades</Typography>
                  </Box>
                  <Tooltip title="View Details"><InfoOutlinedIcon sx={{ color: '#D4D4D4', fontSize: 16, '&:hover': { color: '#737373' } }} /></Tooltip>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} md={4} lg={2.4}>
              <Paper elevation={0} onClick={() => setDealerPopupOpen(true)} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E5E5', height: '100%', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: '#A3A3A3' } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#737373', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>Revenue</Typography>
                    <Typography sx={{ color: '#0A0A0A', fontWeight: 600, mt: 0.25, lineHeight: 1.1, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(periodAnalytics.brokerage)}</Typography>
                    <Typography variant="caption" sx={{ color: '#A3A3A3', mt: 0.5, display: 'block', fontSize: '0.62rem' }}>brokerage earned</Typography>
                  </Box>
                  <Tooltip title="Dealer Revenue Breakdown"><InfoOutlinedIcon sx={{ color: '#D4D4D4', fontSize: 16, '&:hover': { color: '#737373' } }} /></Tooltip>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} md={4} lg={2.4}>
              <Paper elevation={0} onClick={() => setTurnoverPopup(true)} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E5E5', height: '100%', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: '#A3A3A3' } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#737373', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>Turnover</Typography>
                    <Typography sx={{ color: '#0A0A0A', fontWeight: 600, mt: 0.25, lineHeight: 1.1, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(periodAnalytics.turnover)}</Typography>
                    <Typography variant="caption" sx={{ color: '#A3A3A3', mt: 0.5, display: 'block', fontSize: '0.62rem' }}>{periodAnalytics.optionLots} option lots</Typography>
                  </Box>
                  <Tooltip title="Turnover Breakdown"><InfoOutlinedIcon sx={{ color: '#D4D4D4', fontSize: 16, '&:hover': { color: '#737373' } }} /></Tooltip>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} md={4} lg={2.4}>
              <Paper elevation={0} onClick={() => setRealisedPnlPopup(true)} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E5E5', height: '100%', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: '#A3A3A3' } }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" sx={{ color: '#737373', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>Realised P&L</Typography>
                    <Typography sx={{ color: periodAnalytics.realizedPnL >= 0 ? '#15803D' : '#B91C1C', fontWeight: 600, mt: 0.25, lineHeight: 1.1, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>{formatCurrency(periodAnalytics.realizedPnL)}</Typography>
                    <Typography variant="caption" sx={{ color: '#A3A3A3', mt: 0.5, display: 'block', fontSize: '0.62rem' }}>booked P&L</Typography>
                  </Box>
                  <Tooltip title="P&L Breakdown"><InfoOutlinedIcon sx={{ color: '#D4D4D4', fontSize: 16, '&:hover': { color: '#737373' } }} /></Tooltip>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={6} md={4} lg={2.4}>
              <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#F9FAFB', border: '1px solid #E5E5E5', height: '100%', cursor: 'pointer', transition: 'border-color 0.15s', '&:hover': { borderColor: '#A3A3A3' } }} onClick={() => setBreakevenPopup(true)}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#737373', fontWeight: 500, letterSpacing: '0.04em', textTransform: 'uppercase', fontSize: '0.65rem' }}>Breakeven</Typography>
                  <Typography sx={{ color: '#0A0A0A', fontWeight: 600, mt: 0.25, lineHeight: 1.1, fontSize: '1.35rem', fontVariantNumeric: 'tabular-nums' }}>{mergedPositions.length}<Typography component="span" sx={{ fontSize: '0.85rem', color: '#737373', fontWeight: 400, ml: 0.5 }}>positions</Typography></Typography>
                  <Typography variant="caption" sx={{ color: '#A3A3A3', mt: 0.5, display: 'block', fontSize: '0.62rem' }}>click for details</Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
          {periodView === 'year' && <Box sx={{ px: 2, pb: 1 }}><Typography variant="caption" sx={{ color: COLORS.textSecondary, fontStyle: 'italic', fontSize: '0.6rem' }}>Financial year: April to March</Typography></Box>}
          {periodView === 'month' && <Box sx={{ px: 2, pb: 1 }}><Typography variant="caption" sx={{ color: COLORS.textSecondary, fontStyle: 'italic', fontSize: '0.6rem' }}>Monthly cycle: 26th to 25th</Typography></Box>}
        </Paper>

        {/* URGENT CLOSURE (above open positions per user request) */}
        <Fade in>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${COLORS.negative}25`, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.negative}25`, bgcolor: '#FEF2F2', display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningAmberIcon sx={{ color: COLORS.negative, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.negativeDark, fontSize: '0.9rem' }}>Urgent Closure</Typography>
              <Typography variant="caption" sx={{ color: COLORS.textSecondary, ml: 0.5 }}>Positions with charge-inclusive loss ≥ 2% of deployed amount</Typography>
            </Box>
            <UltimateClosureTable positions={allMergedPositions} clients={clients} />
            <UrgentClosureTable positions={allMergedPositions} clients={clients} />
          </Paper>
        </Fade>

        {/* OPEN POSITIONS */}
        <Fade in>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}`, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.cardBg }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShowChartIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.textPrimary, fontSize: '0.9rem' }}>Open Positions</Typography>
                <Chip label={mergedPositions.length} size="small" sx={{ bgcolor: `${COLORS.primary}15`, color: COLORS.primary, fontWeight: 700, fontSize: '0.7rem', height: 22 }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField select size="small" value={clientFilter} onChange={e => setClientFilter(e.target.value)} sx={{ minWidth: 140, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} InputProps={{ startAdornment: <InputAdornment position="start"><FilterListIcon sx={{ fontSize: 16, color: COLORS.textSecondary }} /></InputAdornment> }}>
                  <MenuItem value="all">All Clients</MenuItem>
                  {activeClients.map(c => <MenuItem key={c.clientId} value={c.clientId}>{c.name}</MenuItem>)}
                </TextField>
                <TextField size="small" placeholder="Search scrip..." value={scripFilter} onChange={e => setScripFilter(e.target.value)} sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: '0.8rem' } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: COLORS.textSecondary }} /></InputAdornment> }} />
              </Box>
            </Box>

            <TableContainer sx={{ maxHeight: 420 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { k: 'serialNumber', l: '#', w: 30 },
                      { k: 'clientName', l: 'Client', w: 75 },
                      { k: 'scrip', l: 'Scrip', w: 95 },
                      { k: 'tradeType', l: 'Type', w: 50 },
                      { k: 'direction', l: 'Dir', w: 28 },
                      { k: 'createdAt', l: 'Date', w: 70 },
                      { k: 'balanceLots', l: 'Lots', w: 40, a: 'right' as const },
                      { k: '_shares', l: 'Sh', w: 45, a: 'right' as const },
                      { k: 'purchasePrice', l: 'Avg', w: 65, a: 'right' as const },
                      { k: 'currentPrice', l: 'CMP', w: 65, a: 'right' as const },
                      { k: 'investedAmount', l: 'Invested', w: 75, a: 'right' as const },
                      { k: 'currentValue', l: 'Curr', w: 70, a: 'right' as const },
                      { k: 'unrealizedPnL', l: 'Unreal', w: 70, a: 'right' as const },
                      { k: 'realizedPnL', l: 'Real', w: 60, a: 'right' as const },
                      { k: 'totalPnL', l: 'P&L', w: 70, a: 'right' as const },
                      { k: 'pnlPercentage', l: '%', w: 55, a: 'right' as const },
                      { k: '_slRate', l: 'SL Rate', w: 70, a: 'right' as const, highlight: true },
                      { k: '_slProgress', l: 'SL%', w: 55, a: 'right' as const, highlight: true },
                      { k: 'intradayBE', l: 'Intra', w: 65, a: 'right' as const },
                      { k: 'deliveryBE', l: 'Del', w: 65, a: 'right' as const },
                      { k: 'fullClearBE', l: 'Clear', w: 65, a: 'right' as const },
                      { k: 'carryForwardBE', l: 'CF', w: 60, a: 'right' as const },
                      { k: '_edit', l: '', w: 40 },
                    ].map(col => (
                      <TableCell key={col.k} align={col.a || 'left'} sx={{ bgcolor: (col as any).highlight ? '#FEF3C7' : '#F9FAFB', fontWeight: 700, fontSize: '0.6rem', color: (col as any).highlight ? '#92400E' : COLORS.textSecondary, textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: `2px solid ${(col as any).highlight ? '#F59E0B' : COLORS.border}`, py: 0.5, px: 0.5, width: col.w }}>
                        <TableSortLabel active={sortConfig.key === col.k} direction={sortConfig.key === col.k ? sortConfig.direction : 'desc'} onClick={() => handleSort(col.k)}>{col.l}</TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPositions.length === 0 ? (
                    <TableRow><TableCell colSpan={23} align="center" sx={{ py: 4 }}><Typography variant="body2" color="text.secondary">No open positions</Typography></TableCell></TableRow>
                  ) : sortedPositions.map((t, idx) => {
                    const prof = (t.totalPnL || 0) >= 0;
                    const eq = isEquityType(t.tradeType);
                    const shares = t.shares;
                    const slInfo = slInfoMap.get(t.key);
                    const slPct = slInfo?.slProgressPct ?? 0;
                    // Severity bands: 75/80/85/90/95/100
                    const slWarn = slPct >= 75 && slPct < 100;
                    const slBreached = slPct >= 100;
                    // Choose blink intensity based on threshold (faster as it gets worse)
                    const blinkSpeed = slPct >= 95 ? '0.5s' : slPct >= 90 ? '0.7s' : slPct >= 85 ? '0.9s' : slPct >= 80 ? '1.1s' : '1.4s';
                    const rowBlinkAnim = slWarn ? `slBlink ${blinkSpeed} ease-in-out infinite` : 'none';
                    // Crossed threshold label (75/80/85/90/95, or BREACHED for >= 100)
                    const slThresholdLabel = slBreached ? 'SL BREACHED' : slPct >= 95 ? '95% of SL' : slPct >= 90 ? '90% of SL' : slPct >= 85 ? '85% of SL' : slPct >= 80 ? '80% of SL' : slPct >= 75 ? '75% of SL' : '';
                    return (
                      <TableRow key={t.key} hover sx={{
                        cursor: 'pointer',
                        '&:hover': { bgcolor: `${COLORS.primary}05` },
                        borderLeft: `3px solid ${slBreached ? COLORS.negative : prof ? COLORS.positive : COLORS.negative}`,
                        animation: rowBlinkAnim,
                        '@keyframes slBlink': {
                          '0%, 100%': { backgroundColor: 'transparent' },
                          '50%': { backgroundColor: '#FEE2E2' },
                        },
                      }}
                        onClick={() => setPositionDetail(t)}
                      >
                        <TableCell sx={{ fontWeight: 600, color: COLORS.textSecondary, fontSize: '0.75rem', px: 1 }}>{t.serialNumber || idx + 1}</TableCell>
                        <TableCell sx={{ px: 1 }}><Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.textPrimary, fontSize: '0.75rem' }}>{t.clientName}</Typography></TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <Tooltip title={t.legSummary || ''} arrow placement="top">
                            <Chip label={t.scrip} size="small" sx={{ fontWeight: 700, fontSize: '0.65rem', bgcolor: `${COLORS.primary}10`, color: COLORS.primary, height: 22 }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap' }}>
                            <Chip label={eq ? 'Eq' : isFutureType(t.tradeType) ? 'Fut' : 'Opt'} size="small" variant="outlined" sx={{ fontSize: '0.6rem', height: 20, fontWeight: 600 }} />
                            {/* Equity: Intraday if buy date = today (IST), Delivery if older.
                                Uses t.date (the trade's buy date) for merged positions,
                                falls back to earliest leg's createdAt. */}
                            {eq && (() => {
                              if (t.tradeType === TradeType.INTRA_STOCK) return <Chip label="Intraday" size="small" sx={{ fontSize: '0.55rem', height: 18, fontWeight: 700, bgcolor: '#FCE7F3', color: '#BE185D' }} />;
                              const earliest = t.tradeLegs?.length ? t.tradeLegs.reduce((a: any, b: any) => new Date(a.date || a.createdAt) < new Date(b.date || b.createdAt) ? a : b) : null;
                              const dateStr = earliest?.date || t.createdAt || '';
                              const d = new Date(dateStr);
                              if (isNaN(d.getTime())) return null;
                              const buyDay = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                              const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
                              return buyDay === today
                                ? <Chip label="Intraday" size="small" sx={{ fontSize: '0.55rem', height: 18, fontWeight: 700, bgcolor: '#FCE7F3', color: '#BE185D' }} />
                                : <Chip label="Delivery" size="small" sx={{ fontSize: '0.55rem', height: 18, fontWeight: 700, bgcolor: '#DBEAFE', color: '#1D4ED8' }} />;
                            })()}
                            {isOptionType(t.tradeType) && (t.scrip.toUpperCase().endsWith('CE') || t.scrip.toUpperCase().endsWith('PE')) && (
                              <Chip
                                label={t.scrip.toUpperCase().endsWith('CE') ? 'Call' : 'Put'}
                                size="small"
                                sx={{
                                  fontSize: '0.55rem', height: 18, fontWeight: 700,
                                  bgcolor: t.scrip.toUpperCase().endsWith('CE') ? '#DCFCE7' : '#FEE2E2',
                                  color: t.scrip.toUpperCase().endsWith('CE') ? '#047857' : '#B91C1C',
                                }}
                              />
                            )}
                            {t.isMTF && <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: '#92400E', bgcolor: '#FEF3C7', px: 0.4, py: 0.1, borderRadius: 0.5, lineHeight: 1.2, whiteSpace: 'nowrap' }}>MTF{t.leverage ? ` ${t.leverage}` : ''}</Typography>}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ px: 0.5 }}><Chip label={t.direction === 'SELL' ? 'S' : 'B'} size="small" sx={{ fontSize: '0.6rem', height: 18, fontWeight: 700, minWidth: 22, bgcolor: t.direction === 'SELL' ? COLORS.negativeLight : COLORS.positiveLight, color: t.direction === 'SELL' ? COLORS.negative : COLORS.positive }} /></TableCell>
                        <TableCell sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, fontWeight: 600, whiteSpace: 'nowrap', px: 1 }}>
                          {t.createdAt ? (<>{new Date(t.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })}<Typography component="span" sx={{ fontSize: '0.6rem', color: COLORS.textSecondary, ml: 0.5 }}>{new Date(t.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}</Typography></>) : '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1, color: eq ? COLORS.textSecondary : COLORS.textPrimary }}>{eq ? '—' : t.balanceLots}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', px: 1 }}>{shares}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', px: 1 }}>{formatCurrencyFull(t.weightedAvgBuyPrice)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, px: 1 }}>{formatCurrencyFull(t.currentPrice)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', px: 1 }}>{formatCurrency(t.investedAmount)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, px: 1 }}>{formatCurrency(t.currentValue)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, fontSize: '0.75rem', color: (t.unrealizedPnL || 0) >= 0 ? COLORS.positive : COLORS.negative, px: 1 }}>{formatCurrency(t.unrealizedPnL)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: '0.75rem', color: (t.realizedPnL || 0) >= 0 ? COLORS.positive : COLORS.negative, px: 1 }}>{formatCurrency(t.realizedPnL)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: prof ? COLORS.positive : COLORS.negative, px: 1 }}>{formatCurrency(t.totalPnL)}</TableCell>
                        <TableCell align="right" sx={{ px: 1 }}><Chip label={formatPercent(t.pnlPercentage)} size="small" sx={{ fontWeight: 700, fontSize: '0.6rem', bgcolor: prof ? COLORS.positiveLight : COLORS.negativeLight, color: prof ? COLORS.positive : COLORS.negativeDark, height: 20 }} /></TableCell>
                        {/* SL Rate (highlighted) */}
                        <TableCell align="right" sx={{ px: 1, bgcolor: slBreached ? '#FEE2E2' : slWarn ? '#FEF3C7' : '#FFFBEB', borderLeft: '2px solid #F59E0B', borderRight: '2px solid #F59E0B' }}>
                          {slInfo ? (
                            <Box>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: slBreached ? COLORS.negativeDark : '#92400E' }}>{formatCurrencyFull(slInfo.slRate)}</Typography>
                              <Typography sx={{ fontSize: '0.5rem', color: COLORS.textSecondary, lineHeight: 1 }}>{slInfo.isFuture ? `${t.balanceLots} lot SL` : '2% dep'}</Typography>
                            </Box>
                          ) : <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>—</Typography>}
                        </TableCell>
                        {/* SL Progress % (highlighted, blinks when ≥75%) */}
                        <TableCell align="right" sx={{ px: 1, bgcolor: slBreached ? '#FEE2E2' : slWarn ? '#FEF3C7' : '#FFFBEB', borderRight: '2px solid #F59E0B' }}>
                          {slInfo ? (
                            <Box>
                              <Chip
                                label={`${slPct.toFixed(0)}%`}
                                size="small"
                                sx={{
                                  fontWeight: 800, fontSize: '0.65rem', height: 20,
                                  bgcolor: slBreached ? COLORS.negative : slPct >= 90 ? '#DC2626' : slPct >= 80 ? '#F59E0B' : slPct >= 75 ? '#FB923C' : '#E5E7EB',
                                  color: slPct >= 75 || slBreached ? '#fff' : COLORS.textSecondary,
                                }}
                              />
                              {slThresholdLabel && (
                                <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: COLORS.negativeDark, lineHeight: 1.1, mt: 0.2 }}>
                                  {slBreached ? slThresholdLabel : `${slThresholdLabel} reached`}
                                </Typography>
                              )}
                            </Box>
                          ) : <Typography sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>—</Typography>}
                        </TableCell>
                        {/* Intra BE */}
                        {(() => {
                          const beProfit = (be: number) => t.direction === 'SELL' ? t.currentPrice <= be : t.currentPrice >= be;
                          return (<>
                        <TableCell align="right" sx={{ fontSize: '0.7rem', px: 1, fontWeight: 600, color: isEquityTradeType(t.tradeType) ? (beProfit(t.breakeven?.intradayBE || 0) ? COLORS.positive : COLORS.negative) : COLORS.textSecondary }}>
                          {isEquityTradeType(t.tradeType) && t.breakeven?.intradayBE ? formatCurrencyFull(t.breakeven.intradayBE) : (t.breakeven?.futureBE ? formatCurrencyFull(t.breakeven.futureBE) : (t.breakeven?.optionBE ? formatCurrencyFull(t.breakeven.optionBE) : '—'))}
                        </TableCell>
                        {/* Del BE */}
                        <TableCell align="right" sx={{ fontSize: '0.7rem', px: 1, fontWeight: 600, color: isEquityTradeType(t.tradeType) ? (beProfit(t.breakeven?.deliveryBE || 0) ? COLORS.positive : COLORS.negative) : COLORS.textSecondary }}>
                          {isEquityTradeType(t.tradeType) && t.breakeven?.deliveryBE ? formatCurrencyFull(t.breakeven.deliveryBE) : '—'}
                        </TableCell>
                        {/* Clear BE */}
                        <TableCell align="right" sx={{ fontSize: '0.7rem', px: 1, fontWeight: 600, color: isEquityTradeType(t.tradeType) ? (beProfit(t.breakeven?.fullClearBE || 0) ? COLORS.positive : COLORS.negative) : COLORS.textSecondary }}>
                          {isEquityTradeType(t.tradeType) && t.breakeven?.fullClearBE ? formatCurrencyFull(t.breakeven.fullClearBE) : '—'}
                        </TableCell>
                        {/* Carry Forward BE */}
                        <TableCell align="right" sx={{ fontSize: '0.7rem', px: 1, fontWeight: 600, color: isEquityTradeType(t.tradeType) ? (beProfit(t.breakeven?.carryForwardBE || 0) ? COLORS.positive : COLORS.negative) : COLORS.textSecondary }}>
                          {isEquityTradeType(t.tradeType) && t.breakeven?.carryForwardBE ? formatCurrencyFull(t.breakeven.carryForwardBE) : '—'}
                        </TableCell>
                          </>);
                        })()}
                        {/* Actions: Add + Edit */}
                        <TableCell sx={{ px: 0.5, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Add to position" arrow>
                            <IconButton size="small" sx={{ p: 0.3, color: COLORS.positive, '&:hover': { color: '#047857' } }}
                              onClick={() => {
                                const eqType = isEquityTradeType(t.tradeType);
                                setPrefillData({ clientId: t.clientId, scrip: t.scrip, tradeType: eqType ? 'Equity' : isFutureType(t.tradeType) ? 'Future' : 'Option', direction: t.direction });
                                setCurrentBreakeven(t.breakeven);
                                setEditingTrade(null);
                                setShowTradeForm(true);
                              }}>
                              <AddIcon sx={{ fontSize: 15 }} />
                            </IconButton>
                          </Tooltip>
                          <IconButton size="small" sx={{ p: 0.3, color: COLORS.textSecondary, '&:hover': { color: COLORS.primary } }}
                            onClick={(e) => { e.stopPropagation(); setPositionDetail(t); }}>
                            <EditIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Fade>

        {/* CLIENT BREAKDOWN (full row) */}
        <Fade in>
          <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${COLORS.border}`, overflow: 'hidden', mb: 2 }}>
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${COLORS.border}`, bgcolor: COLORS.cardBg, display: 'flex', alignItems: 'center', gap: 1 }}>
              <PeopleIcon sx={{ color: COLORS.primary, fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: COLORS.textPrimary, fontSize: '0.9rem' }}>Client Breakdown</Typography>
              <Chip label={`${dashboard?.activeClients || 0} active`} size="small" sx={{ bgcolor: `${COLORS.primary}15`, color: COLORS.primary, fontWeight: 600, fontSize: '0.65rem', height: 20 }} />
            </Box>
            <ClientBreakdownTable trades={trades} clients={clients} />
          </Paper>
        </Fade>

{/* Revenue & Activity removed from bottom - now at top as stat cards */}
      </Box>

      {/* Trade Dialog */}
      <TradeDialog key={editingTrade ? editingTrade.tradeId : prefillData ? `prefill-${prefillData.scrip}` : quickEntryQuery ? `quick-${quickEntryQuery}` : 'new'} open={showTradeForm} onClose={() => { setShowTradeForm(false); setEditingTrade(null); setPrefillData(null); setCurrentBreakeven(undefined); }} onSuccess={() => { setShowTradeForm(false); setEditingTrade(null); setPrefillData(null); setCurrentBreakeven(undefined); fetchData(); }} clients={clients} editTrade={editingTrade} existingTrades={trades} prefillData={prefillData || undefined} prefillClientQuery={quickEntryQuery} breakeven={currentBreakeven} />

      {/* Dealer Revenue Popup */}
      <DealerRevenuePopup open={dealerPopupOpen} onClose={() => setDealerPopupOpen(false)} trades={trades} clients={clients} />

      {/* Commission Calculator */}
      <CommissionCalculator open={commissionOpen} onClose={() => setCommissionOpen(false)} trades={trades} clients={clients} employees={employees} />

      {/* Card detail popups */}
      <OpenTradesPopup open={openTradesPopup} onClose={() => setOpenTradesPopup(false)} trades={trades} />
      <TurnoverPopup open={turnoverPopup} onClose={() => setTurnoverPopup(false)} trades={trades} clients={clients} />
      <RealisedPnlPopup open={realisedPnlPopup} onClose={() => setRealisedPnlPopup(false)} trades={trades} clients={clients} />
      <BreakevenPopup open={breakevenPopup} onClose={() => setBreakevenPopup(false)} positions={mergedPositions} />

      {/* Position Detail Dialog — shows all legs of a merged position */}
      <Dialog open={!!positionDetail} onClose={() => { setPositionDetail(null); setLifoSellForm({ show: false, qty: '', price: '', loading: false, error: null }); }} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, mx: 'auto' } }} sx={{ '& .MuiDialog-container': { alignItems: 'center' } }}>
        {positionDetail && (
          <>
            <DialogTitle sx={{ bgcolor: COLORS.primary, color: '#fff', py: 1.5, px: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} color="#fff">
                  {positionDetail.clientName} — {positionDetail.scrip}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>
                  {(() => {
                    const isFO = isFutureType(positionDetail.tradeType) || isOptionType(positionDetail.tradeType);
                    const ls = positionDetail.tradeLegs[0]?.lotSize || 1;
                    if (isFO) {
                      const totalLots = Math.round(positionDetail.shares / ls);
                      return ls > 1
                        ? `${totalLots} lots × ${ls} = ${positionDetail.shares} shares`
                        : `${totalLots} lots (${positionDetail.shares} shares)`;
                    }
                    return `${positionDetail.shares} shares`;
                  })()} | {positionDetail.direction === 'SELL' ? 'Avg Sell' : 'Avg Buy'}: {formatCurrencyFull(positionDetail.weightedAvgBuyPrice)} | {positionDetail.tradeLegs.length} trade leg{positionDetail.tradeLegs.length > 1 ? 's' : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  <Chip label={isEquityType(positionDetail.tradeType) ? 'Equity' : isFutureType(positionDetail.tradeType) ? 'Future' : 'Option'} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, bgcolor: 'rgba(255,255,255,0.2)', color: '#fff' }} />
                  {isOptionType(positionDetail.tradeType) && (
                    <Chip label={positionDetail.scrip.toUpperCase().endsWith('CE') ? 'Call' : 'Put'} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, bgcolor: positionDetail.scrip.toUpperCase().endsWith('CE') ? '#DBEAFE' : '#FCE7F3', color: positionDetail.scrip.toUpperCase().endsWith('CE') ? '#1D4ED8' : '#BE185D' }} />
                  )}
                  <Chip label={positionDetail.direction === 'SELL' ? 'SELL' : 'BUY'} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, bgcolor: positionDetail.direction === 'SELL' ? 'rgba(239,68,68,0.3)' : 'rgba(16,185,129,0.3)', color: '#fff' }} />
                  <Chip label={positionDetail.tradeLegs[0]?.exchange || 'NSE'} size="small" variant="outlined" sx={{ fontSize: '0.55rem', fontWeight: 600, height: 18, borderColor: 'rgba(255,255,255,0.4)', color: 'rgba(255,255,255,0.9)' }} />
                  {positionDetail.isMTF && <Chip label={`MTF ${positionDetail.leverage || ''}`} size="small" sx={{ fontSize: '0.6rem', fontWeight: 700, height: 18, bgcolor: '#FEF3C7', color: '#92400E' }} />}
                </Box>
              </Box>
              <IconButton size="small" onClick={() => setPositionDetail(null)} sx={{ color: '#fff' }}><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 0 }}>
              {/* Trade legs table — read only, no per-leg actions */}
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {(() => {
                        const isFO = isFutureType(positionDetail.tradeType) || isOptionType(positionDetail.tradeType);
                        const isSF = positionDetail.direction === 'SELL';
                        const openLbl = isSF ? 'Sell' : 'Buy';
                        const closeLbl = isSF ? 'Cover' : 'Sold';
                        const headers = isFO
                          ? ['#', 'Date', `${openLbl} Lots`, `${openLbl} Qty`, `${openLbl} Price`, `${closeLbl} Lots`, `${closeLbl} Qty`, `${closeLbl} Price`, 'Bal Lots', 'Bal Qty', 'Invested', 'P&L', '']
                          : ['#', 'Date', `${openLbl} Qty`, `${openLbl} Price`, closeLbl, `${closeLbl} Price`, 'Balance', 'Invested', 'P&L', ''];
                        return headers.map(h => (
                          <TableCell key={h || '_edit'} align={h === '#' || h === 'Date' || h === '' ? 'left' : 'right'}
                            sx={{ fontWeight: 700, fontSize: '0.65rem', color: COLORS.textSecondary, textTransform: 'uppercase', bgcolor: '#F9FAFB', py: 1, borderBottom: `2px solid ${COLORS.border}`, ...(h === '' ? { width: 30, px: 0.5 } : {}), ...(h.includes('Lots') ? { color: '#0A0A0A', fontSize: '0.6rem' } : {}) }}>
                            {h}
                          </TableCell>
                        ));
                      })()}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {positionDetail.tradeLegs
                      .sort((a, b) => new Date(a.createdAt || a.date).getTime() - new Date(b.createdAt || b.date).getTime())
                      .map((leg, i) => {
                        const bal = leg.purchaseLots - (leg.saleLots || 0);
                        const legInvested = bal * leg.lotSize * leg.purchasePrice;
                        const legPnL = leg.totalPnL || 0;
                        const isFO = isFutureType(positionDetail.tradeType) || isOptionType(positionDetail.tradeType);
                        const dateCell = (
                          <TableCell sx={{ fontSize: '0.7rem', color: COLORS.textSecondary, whiteSpace: 'nowrap' }}>
                            {new Date(leg.date || leg.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' })}
                            <Typography component="span" sx={{ fontSize: '0.6rem', ml: 0.5 }}>
                              {new Date(leg.date || leg.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' })}
                            </Typography>
                          </TableCell>
                        );
                        const editCell = (
                          <TableCell sx={{ px: 0.5 }}>
                            <IconButton size="small" sx={{ p: 0.2, color: COLORS.textSecondary, '&:hover': { color: COLORS.primary } }}
                              onClick={() => { setPositionDetail(null); setCurrentBreakeven(positionDetail.breakeven); setEditingTrade(leg); setShowTradeForm(true); }}>
                              <EditIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </TableCell>
                        );

                        if (isFO) {
                          return (
                            <TableRow key={leg.tradeId} hover sx={{ '&:hover': { bgcolor: `${COLORS.primary}05` } }}>
                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.textSecondary }}>{i + 1}</TableCell>
                              {dateCell}
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#0A0A0A' }}>{leg.purchaseLots}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>{leg.purchaseLots * leg.lotSize}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrencyFull(leg.purchasePrice)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem', color: (leg.saleLots || 0) > 0 ? COLORS.negative : COLORS.textSecondary, fontWeight: (leg.saleLots || 0) > 0 ? 700 : 400 }}>
                                {(leg.saleLots || 0) > 0 ? (leg.saleLots || 0) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                                {(leg.saleLots || 0) > 0 ? (leg.saleLots || 0) * leg.lotSize : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                                {(leg.salePrice || 0) > 0 ? formatCurrencyFull(leg.salePrice) : '—'}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: bal > 0 ? '#0A0A0A' : COLORS.textSecondary }}>
                                {bal}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.7rem', color: COLORS.textSecondary }}>
                                {bal * leg.lotSize}
                              </TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(legInvested)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: legPnL >= 0 ? COLORS.positive : COLORS.negative }}>
                                {legPnL !== 0 ? formatCurrency(legPnL) : '—'}
                              </TableCell>
                              {editCell}
                            </TableRow>
                          );
                        }

                        return (
                          <TableRow key={leg.tradeId} hover sx={{ '&:hover': { bgcolor: `${COLORS.primary}05` } }}>
                            <TableCell sx={{ fontSize: '0.75rem', fontWeight: 600, color: COLORS.textSecondary }}>{i + 1}</TableCell>
                            {dateCell}
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{leg.purchaseLots * leg.lotSize}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrencyFull(leg.purchasePrice)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', color: (leg.saleLots || 0) > 0 ? COLORS.negative : COLORS.textSecondary }}>
                              {(leg.saleLots || 0) > 0 ? (leg.saleLots || 0) * leg.lotSize : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
                              {(leg.salePrice || 0) > 0 ? formatCurrencyFull(leg.salePrice) : '—'}
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: bal > 0 ? COLORS.primary : COLORS.textSecondary }}>
                              {bal * leg.lotSize}
                            </TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{formatCurrency(legInvested)}</TableCell>
                            <TableCell align="right" sx={{ fontSize: '0.75rem', fontWeight: 600, color: legPnL >= 0 ? COLORS.positive : COLORS.negative }}>
                              {legPnL !== 0 ? formatCurrency(legPnL) : '—'}
                            </TableCell>
                            {editCell}
                          </TableRow>
                        );
                      })}
                    {/* Total row */}
                    {(() => {
                      const isFO = isFutureType(positionDetail.tradeType) || isOptionType(positionDetail.tradeType);
                      const totalBuyLots = positionDetail.tradeLegs.reduce((s: number, l: any) => s + l.purchaseLots, 0);
                      const totalBuyQty = positionDetail.tradeLegs.reduce((s: number, l: any) => s + l.purchaseLots * l.lotSize, 0);
                      const totalSoldLots = positionDetail.tradeLegs.reduce((s: number, l: any) => s + (l.saleLots || 0), 0);
                      const totalSoldQty = positionDetail.tradeLegs.reduce((s: number, l: any) => s + (l.saleLots || 0) * l.lotSize, 0);
                      const ls = positionDetail.tradeLegs[0]?.lotSize || 1;
                      const balLots = Math.round(positionDetail.shares / ls);

                      if (isFO) {
                        return (
                          <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                            <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#0A0A0A' }}>{totalBuyLots}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: COLORS.textSecondary }}>{totalBuyQty}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{formatCurrencyFull(positionDetail.weightedAvgBuyPrice)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: COLORS.negative }}>{totalSoldLots || '—'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: COLORS.textSecondary }}>{totalSoldQty || '—'}</TableCell>
                            <TableCell />
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#0A0A0A' }}>{balLots}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', color: COLORS.textSecondary }}>{positionDetail.shares}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{formatCurrency(positionDetail.investedAmount)}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: positionDetail.totalPnL >= 0 ? COLORS.positive : COLORS.negative }}>
                              {formatCurrency(positionDetail.totalPnL)}
                            </TableCell>
                          </TableRow>
                        );
                      }
                      return (
                        <TableRow sx={{ bgcolor: '#F9FAFB' }}>
                          <TableCell colSpan={2} sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Total</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{totalBuyQty}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{formatCurrencyFull(positionDetail.weightedAvgBuyPrice)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: COLORS.negative }}>{totalSoldQty || '—'}</TableCell>
                          <TableCell />
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: COLORS.primary }}>{positionDetail.shares}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>{formatCurrency(positionDetail.investedAmount)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.75rem', color: positionDetail.totalPnL >= 0 ? COLORS.positive : COLORS.negative }}>
                            {formatCurrency(positionDetail.totalPnL)}
                          </TableCell>
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
              {/* Breakeven summary */}
              <Box sx={{ p: 2, bgcolor: '#F5F3FF', borderTop: `1px solid ${COLORS.border}` }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>Breakeven Rates</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {(() => {
                    const be = positionDetail.breakeven;
                    const cmp = positionDetail.currentPrice;
                    const qty = positionDetail.shares;
                    const items: { label: string; value?: number }[] = [];
                    if (be.intradayBE) items.push({ label: 'Intra BE', value: be.intradayBE });
                    if (be.deliveryBE) items.push({ label: 'Delivery BE', value: be.deliveryBE });
                    if (be.fullClearBE) items.push({ label: 'Full Clear BE', value: be.fullClearBE });
                    if (be.carryForwardBE) items.push({ label: 'Carry Fwd BE', value: be.carryForwardBE });
                    if (be.futureBE) items.push({ label: 'Future BE', value: be.futureBE });
                    if (be.optionBE) items.push({ label: 'Option BE', value: be.optionBE });
                    return items.map(item => {
                      const pnl = item.value ? (cmp - item.value) * qty : 0;
                      return (
                        <Box key={item.label}>
                          <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.6rem' }}>{item.label}</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: cmp >= (item.value || 0) ? COLORS.positive : COLORS.negative }}>
                            {item.value ? formatCurrencyFull(item.value) : '—'}
                          </Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.6rem', color: pnl >= 0 ? COLORS.positive : COLORS.negative }}>
                            {pnl >= 0 ? '+' : ''}{formatCurrency(Math.round(pnl))}
                          </Typography>
                        </Box>
                      );
                    });
                  })()}
                  <Box>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.6rem' }}>CMP</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{formatCurrencyFull(positionDetail.currentPrice)}</Typography>
                  </Box>
                </Box>
              </Box>

              {/* Charges breakdown — equity: split by today/older shares */}
              {(() => {
                const eq = isEquityTradeType(positionDetail.tradeType);
                const isFut = isFutureType(positionDetail.tradeType);
                const client = clients.find(c => c.clientId === positionDetail.clientId);
                // Read exchange from first trade leg (all legs in same position use same exchange)
                const posExch: 'NSE' | 'BSE' = (positionDetail.tradeLegs[0]?.exchange as 'NSE' | 'BSE') || 'NSE';
                const be = positionDetail.breakeven;
                const tShares = be?.todayShares || 0;
                const tCost = be?.todayCost || 0;
                const oShares = be?.olderShares || 0;
                const oCost = be?.olderCost || 0;

                if (eq) {
                  const delRate = client?.brokerageRates?.stock ?? 0.003;
                  const intraRate = client?.brokerageRates?.intraStock ?? 0.0003;
                  const renderTip = (label: string, shares: number, ch: any, isIntra: boolean) => {
                    const brkRate = isIntra ? intraRate : delRate;
                    const brkPct = (brkRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';
                    const sttPct = isIntra ? `${(CHARGE_RATES.STT_EQUITY_INTRADAY_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell` : `${(CHARGE_RATES.STT_EQUITY_DELIVERY_BUY * 100).toFixed(1)}% buy + ${(CHARGE_RATES.STT_EQUITY_DELIVERY_SELL * 100).toFixed(1)}% sell`;
                    const eqExchRate = posExch === 'BSE' ? CHARGE_RATES.EXCHANGE_EQUITY_BSE : CHARGE_RATES.EXCHANGE_EQUITY_NSE;
                    const exchPct = (eqExchRate * 100).toFixed(5).replace(/\.?0+$/, '') + '% ' + posExch;
                    const sebiPct = (CHARGE_RATES.SEBI_RATE * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                    const stampRate = isIntra ? CHARGE_RATES.STAMP_EQUITY_INTRADAY : CHARGE_RATES.STAMP_EQUITY_DELIVERY;
                    const stampPct = (stampRate * 100).toFixed(7).replace(/\.?0+$/, '') + '%';
                    const gstPct = (CHARGE_RATES.GST_RATE * 100).toFixed(0) + '%';
                    return (
                      <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
                        <div><b>{label} — {positionDetail.direction === 'SELL' ? 'Sell' : 'Buy'} Side ({shares} shares)</b></div>
                        <div>Brokerage ({brkPct}): ₹{ch.brokerage.toFixed(7)}</div>
                        <div>STT ({sttPct}): ₹{ch.stt.toFixed(7)}</div>
                        <div>Exchange ({exchPct}): ₹{ch.exchangeCharges.toFixed(7)}</div>
                        <div>SEBI ({sebiPct}): ₹{ch.sebiCharges.toFixed(7)}</div>
                        <div>Stamp ({stampPct} buy): ₹{ch.stampDuty.toFixed(7)}</div>
                        <div>GST ({gstPct} on Brk+Exch+SEBI): ₹{ch.gst.toFixed(7)}</div>
                        <div><b>Total: ₹{ch.totalCharges.toFixed(7)}</b></div>
                      </Box>
                    );
                  };
                  const delPct = (delRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';
                  const intraPct = (intraRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';
                  // Calculate on actual share portions — for sell-first, open side is a sell
                  const eqSF = positionDetail.direction === 'SELL';
                  const delBuy = oShares > 0 ? (eqSF
                    ? calculateChargesBreakdown(TradeType.EQUITY, 0, oCost, delRate, false, posExch)
                    : calculateChargesBreakdown(TradeType.EQUITY, oCost, 0, delRate, false, posExch)) : null;
                  const intraBuy = tShares > 0 ? (eqSF
                    ? calculateChargesBreakdown(TradeType.INTRA_STOCK, 0, tCost, intraRate, true, posExch)
                    : calculateChargesBreakdown(TradeType.INTRA_STOCK, tCost, 0, intraRate, true, posExch)) : null;
                  const totalBuyCharges = (delBuy?.totalCharges || 0) + (intraBuy?.totalCharges || 0);

                  return (
                    <Box sx={{ px: 2, py: 1, bgcolor: '#FFF7ED', borderTop: `1px solid ${COLORS.border}`, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      {delBuy && (
                        <Tooltip title={renderTip(`Delivery (${delPct})`, oShares, delBuy, false)} arrow placement="top">
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#2563EB', fontSize: '0.68rem', cursor: 'help' }}>
                            Delivery: ₹{delBuy.totalCharges.toFixed(7)} <Box component="span" sx={{ fontSize: '0.55rem', color: '#9CA3AF' }}>({oShares} shares)</Box>
                          </Typography>
                        </Tooltip>
                      )}
                      {intraBuy && (
                        <Tooltip title={renderTip(`Intraday (${intraPct})`, tShares, intraBuy, true)} arrow placement="top">
                          <Typography variant="caption" sx={{ fontWeight: 700, color: '#059669', fontSize: '0.68rem', cursor: 'help' }}>
                            Intraday: ₹{intraBuy.totalCharges.toFixed(7)} <Box component="span" sx={{ fontSize: '0.55rem', color: '#9CA3AF' }}>({tShares} shares)</Box>
                          </Typography>
                        </Tooltip>
                      )}
                      {delBuy && intraBuy && (
                        <Typography variant="caption" sx={{ fontWeight: 700, color: '#0A0A0A', fontSize: '0.68rem' }}>
                          Total: ₹{totalBuyCharges.toFixed(7)} <Box component="span" sx={{ fontSize: '0.55rem', color: '#9CA3AF' }}>(buy)</Box>
                        </Typography>
                      )}
                    </Box>
                  );
                }

                // F&O: single charge
                const foBuyValue = positionDetail.investedAmount;
                const tradeType = isFut ? TradeType.FUTURE : TradeType.OPTION;
                const isOpt = !isFut;

                // For options: calculate per-lot brokerage (₹X × lots)
                let fixedBrk: number | undefined;
                let optPerLot = 0;
                let totalLots = 0;
                if (isOpt) {
                  optPerLot = client?.brokerageRates?.option || DEFAULT_OPTION_BROKERAGE;
                  // Calculate total buy lots from trade legs
                  totalLots = positionDetail.tradeLegs.reduce((s: number, l: any) => {
                    const bal = l.purchaseLots - (l.saleLots || 0);
                    return s + (bal > 0 ? bal : 0);
                  }, 0);
                  fixedBrk = optPerLot * totalLots;
                }

                const brokerageRate = isFut ? CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE : 0;
                // For sell-first: open side is a sell (pass as sellValue), not a buy
                const isSF = positionDetail.direction === 'SELL';
                const buyCharges = isSF
                  ? calculateChargesBreakdown(tradeType, 0, foBuyValue, brokerageRate, false, posExch, fixedBrk)
                  : calculateChargesBreakdown(tradeType, foBuyValue, 0, brokerageRate, false, posExch, fixedBrk);
                if (!buyCharges || buyCharges.totalCharges <= 0) return null;

                // Brokerage label with rate info
                const futRate = client?.brokerageRates?.future || CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE;
                const brkLabel = isOpt
                  ? `Brokerage (₹${optPerLot}/lot × ${totalLots} lots)`
                  : `Brokerage (${(futRate * 100).toFixed(2).replace(/\.?0+$/, '')}%)`;
                const sttPct = isFut
                  ? `${(CHARGE_RATES.STT_FUTURE_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell`
                  : `${(CHARGE_RATES.STT_OPTION_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell`;
                const exchRate = isFut
                  ? (posExch === 'BSE' ? CHARGE_RATES.EXCHANGE_FUTURE_BSE : CHARGE_RATES.EXCHANGE_FUTURE_NSE)
                  : CHARGE_RATES.EXCHANGE_OPTION;
                const exchPct = (exchRate * 100).toFixed(5).replace(/\.?0+$/, '') + '%';
                const sebiPct = (CHARGE_RATES.SEBI_RATE * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                const stampRate = isFut ? CHARGE_RATES.STAMP_FUTURE : CHARGE_RATES.STAMP_OPTION;
                const stampPct = (stampRate * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                const gstPct = (CHARGE_RATES.GST_RATE * 100).toFixed(0) + '%';

                return (
                  <Tooltip title={
                    <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
                      <div><b>Est. {positionDetail.direction === 'SELL' ? 'Sell' : 'Buy'}-Side Charges (open)</b></div>
                      <div>{brkLabel}: ₹{buyCharges.brokerage.toFixed(7)}</div>
                      <div>STT ({sttPct}): ₹{buyCharges.stt.toFixed(7)}</div>
                      <div>Exchange ({exchPct}): ₹{buyCharges.exchangeCharges.toFixed(7)}</div>
                      <div>SEBI ({sebiPct}): ₹{buyCharges.sebiCharges.toFixed(7)}</div>
                      <div>Stamp ({stampPct}): ₹{buyCharges.stampDuty.toFixed(7)}</div>
                      <div>GST ({gstPct}): ₹{buyCharges.gst.toFixed(7)}</div>
                    </Box>
                  } arrow placement="top">
                    <Box sx={{ px: 2, py: 1, bgcolor: '#FFF7ED', borderTop: `1px solid ${COLORS.border}`, cursor: 'help' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: '#D97706', fontSize: '0.7rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {isOpt ? `Options ${totalLots} lots | ` : ''}Est. Charges ({positionDetail.direction === 'SELL' ? 'sell' : 'buy'} side): ₹{buyCharges.totalCharges.toFixed(7)}
                        <Box component="span" sx={{ fontSize: '0.6rem', color: '#9CA3AF' }}>(hover for details)</Box>
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })()}

              {/* LIFO Sell inline form */}
              {lifoSellForm.show ? (
                <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${COLORS.border}`, bgcolor: '#FEF2F2' }}>
                  {(() => {
                    const isFO = isFutureType(positionDetail.tradeType) || isOptionType(positionDetail.tradeType);
                    const ls = positionDetail.tradeLegs[0]?.lotSize || 1;
                    const totalAvailLots = isFO ? Math.round(positionDetail.shares / ls) : 0;
                    const inputLabel = isFO ? 'Lots' : 'Qty';
                    const maxInput = isFO ? totalAvailLots : positionDetail.shares;
                    const availText = isFO
                      ? `Available: ${totalAvailLots} lots (${positionDetail.shares} shares)`
                      : `Available: ${positionDetail.shares} shares`;

                    return (<>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: positionDetail.direction === 'SELL' ? COLORS.positive : COLORS.negative, textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                    {positionDetail.direction === 'SELL' ? 'Cover Position (LIFO)' : 'Sell from Position (LIFO)'}
                  </Typography>
                  {lifoSellForm.error && <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: '0.7rem' }}>{lifoSellForm.error}</Alert>}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField size="small" label={inputLabel} type="number" value={lifoSellForm.qty}
                      onChange={e => setLifoSellForm(p => ({ ...p, qty: e.target.value ? Number(e.target.value) : '', error: null }))}
                      inputProps={{ min: 1, max: maxInput }}
                      sx={{ width: 100, '& .MuiInputBase-input': { fontSize: '0.8rem' } }} />
                    {isFO && lifoSellForm.qty && (
                      <Typography variant="caption" sx={{ color: '#0A0A0A', fontWeight: 600, fontSize: '0.7rem', minWidth: 60 }}>
                        = {Number(lifoSellForm.qty) * ls} shares
                      </Typography>
                    )}
                    <TextField size="small" label={positionDetail.direction === 'SELL' ? 'Cover Price' : 'Sale Price'} type="number" value={lifoSellForm.price}
                      onChange={e => setLifoSellForm(p => ({ ...p, price: e.target.value ? Number(e.target.value) : '', error: null }))}
                      inputProps={{ min: 0.01, step: 0.01 }}
                      sx={{ width: 120, '& .MuiInputBase-input': { fontSize: '0.8rem' } }} />
                    <Button size="small" variant="contained" disabled={lifoSellForm.loading || !lifoSellForm.qty || !lifoSellForm.price}
                      sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', bgcolor: positionDetail.direction === 'SELL' ? COLORS.positive : COLORS.negative, minWidth: 60, '&:hover': { bgcolor: positionDetail.direction === 'SELL' ? '#047857' : '#B91C1C' } }}
                      onClick={async () => {
                        const inputVal = Number(lifoSellForm.qty);
                        const price = Number(lifoSellForm.price);
                        // For F&O: input is lots, convert to shares for the API
                        const sellShares = isFO ? inputVal * ls : inputVal;
                        if (inputVal <= 0 || inputVal > maxInput) {
                          setLifoSellForm(p => ({ ...p, error: `Max ${maxInput} ${isFO ? 'lots' : 'shares'} available` }));
                          return;
                        }
                        setLifoSellForm(p => ({ ...p, loading: true, error: null }));
                        try {
                          await tradeService.lifoSell({
                            clientId: positionDetail.clientId,
                            scrip: positionDetail.scrip,
                            sellShares,
                            salePrice: price,
                          });
                          setLifoSellForm({ show: false, qty: '', price: '', loading: false, error: null });
                          setPositionDetail(null);
                          fetchData();
                        } catch (err: any) {
                          setLifoSellForm(p => ({ ...p, loading: false, error: err.message || 'Failed to sell' }));
                        }
                      }}>
                      {lifoSellForm.loading ? '...' : (positionDetail.direction === 'SELL' ? 'Cover' : 'Sell')}
                    </Button>
                    <Button size="small" sx={{ textTransform: 'none', fontSize: '0.7rem', color: COLORS.textSecondary, minWidth: 'auto' }}
                      onClick={() => setLifoSellForm({ show: false, qty: '', price: '', loading: false, error: null })}>
                      Cancel
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Typography variant="caption" sx={{ color: COLORS.textSecondary, fontSize: '0.6rem' }}>
                      {positionDetail.direction === 'SELL' ? 'Covers newest shorts first.' : 'Sells newest trades first.'} {availText}
                    </Typography>
                    {(() => {
                      const posExch: 'NSE' | 'BSE' = (positionDetail.tradeLegs[0]?.exchange as 'NSE' | 'BSE') || 'NSE';
                      const inputQty = Number(lifoSellForm.qty) || 0;
                      const price = Number(lifoSellForm.price) || 0;
                      if (inputQty <= 0 || price <= 0) return null;
                      const eq = isEquityTradeType(positionDetail.tradeType);
                      const isFut = isFutureType(positionDetail.tradeType);
                      // For F&O, input is lots → convert to shares for charge calc
                      const qty = isFO ? inputQty * ls : inputQty;
                      const closeValue = qty * price;   // LIFO close price
                      const openValue = qty * positionDetail.weightedAvgBuyPrice; // open position cost
                      const isSF = positionDetail.direction === 'SELL';
                      // For charges: sell-first cover is a buy, open is a sell
                      const sellValue = isSF ? openValue : closeValue;
                      const buyValue = isSF ? closeValue : openValue;
                      const client = clients.find(c => c.clientId === positionDetail.clientId);

                      if (eq) {
                        const be = positionDetail.breakeven;
                        const tSharesAvail = be?.todayShares || 0;
                        const oSharesAvail = be?.olderShares || 0;
                        const tCostTotal = be?.todayCost || 0;
                        const oCostTotal = be?.olderCost || 0;
                        // Per-share cost for each portion (actual leg prices, not weighted avg)
                        const intraAvgPrice = tSharesAvail > 0 ? tCostTotal / tSharesAvail : 0;
                        const delAvgPrice = oSharesAvail > 0 ? oCostTotal / oSharesAvail : 0;
                        // LIFO: sell newest (today's) first, then older
                        const intraSellQty = Math.min(qty, tSharesAvail);
                        const delSellQty = Math.min(qty - intraSellQty, oSharesAvail);

                        const delRate = client?.brokerageRates?.stock ?? 0.003;
                        const intraRate = client?.brokerageRates?.intraStock ?? 0.0003;
                        const delPct = (delRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';
                        const intraPct = (intraRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';

                        const renderTip = (label: string, shares: number, buy: any, sell: any, total: any, isIntra: boolean) => {
                          const brkRate = isIntra ? intraRate : delRate;
                          const brkPct = (brkRate * 100).toFixed(2).replace(/\.?0+$/, '') + '%';
                          const sttInfo = isIntra ? `${(CHARGE_RATES.STT_EQUITY_INTRADAY_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell` : `${(CHARGE_RATES.STT_EQUITY_DELIVERY_BUY * 100).toFixed(1)}% buy+sell`;
                          const exchPct = (CHARGE_RATES.EXCHANGE_EQUITY_NSE * 100).toFixed(5).replace(/\.?0+$/, '') + '%';
                          const sebiPct = (CHARGE_RATES.SEBI_RATE * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                          const stampRate = isIntra ? CHARGE_RATES.STAMP_EQUITY_INTRADAY : CHARGE_RATES.STAMP_EQUITY_DELIVERY;
                          const stampPct = (stampRate * 100).toFixed(7).replace(/\.?0+$/, '') + '%';
                          const gstPct = (CHARGE_RATES.GST_RATE * 100).toFixed(0) + '%';
                          return (
                          <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{label} ({shares} shares)</div>
                            <div><b>Buy side:</b></div>
                            <div>&nbsp; Brokerage ({brkPct}): ₹{buy.brokerage.toFixed(7)}</div>
                            <div>&nbsp; STT ({sttInfo}): ₹{buy.stt.toFixed(7)}</div>
                            <div>&nbsp; Exchange ({exchPct}): ₹{buy.exchangeCharges.toFixed(7)}</div>
                            <div>&nbsp; SEBI ({sebiPct}): ₹{buy.sebiCharges.toFixed(7)}</div>
                            <div>&nbsp; Stamp ({stampPct} buy): ₹{buy.stampDuty.toFixed(7)}</div>
                            <div>&nbsp; GST ({gstPct}): ₹{buy.gst.toFixed(7)}</div>
                            <div>&nbsp; <b>Buy Total: ₹{buy.totalCharges.toFixed(7)}</b></div>
                            <div style={{ marginTop: 4 }}><b>Sell side:</b></div>
                            <div>&nbsp; Brokerage ({brkPct}): ₹{sell.brokerage.toFixed(7)}</div>
                            <div>&nbsp; STT ({sttInfo}): ₹{sell.stt.toFixed(7)}</div>
                            <div>&nbsp; Exchange ({exchPct}): ₹{sell.exchangeCharges.toFixed(7)}</div>
                            <div>&nbsp; SEBI ({sebiPct}): ₹{sell.sebiCharges.toFixed(7)}</div>
                            <div>&nbsp; Stamp ({stampPct} buy): ₹{sell.stampDuty.toFixed(7)}</div>
                            <div>&nbsp; GST ({gstPct}): ₹{sell.gst.toFixed(7)}</div>
                            <div>&nbsp; <b>Sell Total: ₹{sell.totalCharges.toFixed(7)}</b></div>
                            <div style={{ marginTop: 4, fontWeight: 700 }}>Total: ₹{total.totalCharges.toFixed(7)}</div>
                          </Box>
                          );
                        };

                        let totalCharges = 0;
                        const items: JSX.Element[] = [];

                        if (intraSellQty > 0) {
                          const iOpenVal = intraSellQty * intraAvgPrice;
                          const iCloseVal = intraSellQty * price;
                          // For sell-first: open is sell, close (cover) is buy
                          const iBuyVal = isSF ? iCloseVal : iOpenVal;
                          const iSellVal = isSF ? iOpenVal : iCloseVal;
                          const iBuy = calculateChargesBreakdown(TradeType.INTRA_STOCK, iBuyVal, 0, intraRate, true, posExch);
                          const iSell = calculateChargesBreakdown(TradeType.INTRA_STOCK, 0, iSellVal, intraRate, true, posExch);
                          const iTotal = calculateChargesBreakdown(TradeType.INTRA_STOCK, iBuyVal, iSellVal, intraRate, true, posExch);
                          totalCharges += iTotal.totalCharges;
                          items.push(
                            <Tooltip key="intra" title={renderTip(`Intraday (${intraPct})`, intraSellQty, iBuy, iSell, iTotal, true)} arrow placement="top">
                              <Typography variant="caption" sx={{ color: '#059669', fontWeight: 700, fontSize: '0.65rem', cursor: 'help' }}>
                                Intraday: ₹{iTotal.totalCharges.toFixed(7)} <Box component="span" sx={{ fontSize: '0.55rem', color: '#9CA3AF' }}>({intraSellQty})</Box>
                              </Typography>
                            </Tooltip>
                          );
                        }
                        if (delSellQty > 0) {
                          const dOpenVal = delSellQty * delAvgPrice;
                          const dCloseVal = delSellQty * price;
                          const dBuyVal = isSF ? dCloseVal : dOpenVal;
                          const dSellVal = isSF ? dOpenVal : dCloseVal;
                          const dBuy = calculateChargesBreakdown(TradeType.EQUITY, dBuyVal, 0, delRate, false, posExch);
                          const dSell = calculateChargesBreakdown(TradeType.EQUITY, 0, dSellVal, delRate, false, posExch);
                          const dTotal = calculateChargesBreakdown(TradeType.EQUITY, dBuyVal, dSellVal, delRate, false, posExch);
                          totalCharges += dTotal.totalCharges;
                          items.push(
                            <Tooltip key="del" title={renderTip(`Delivery (${delPct})`, delSellQty, dBuy, dSell, dTotal, false)} arrow placement="top">
                              <Typography variant="caption" sx={{ color: '#2563EB', fontWeight: 700, fontSize: '0.65rem', cursor: 'help' }}>
                                Delivery: ₹{dTotal.totalCharges.toFixed(7)} <Box component="span" sx={{ fontSize: '0.55rem', color: '#9CA3AF' }}>({delSellQty})</Box>
                              </Typography>
                            </Tooltip>
                          );
                        }
                        if (items.length > 1) {
                          items.push(
                            <Typography key="total" variant="caption" sx={{ color: '#0A0A0A', fontWeight: 700, fontSize: '0.65rem' }}>
                              Total: ₹{totalCharges.toFixed(7)}
                            </Typography>
                          );
                        }
                        return <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>{items}</Box>;
                      }

                      // F&O — separate buy/sell breakdown
                      const tradeType2 = isFut ? TradeType.FUTURE : TradeType.OPTION;
                      const isOpt2 = !isFut;
                      const sellLots2 = isFO ? inputQty : Math.round(qty / ls);
                      let buyFixedBrk2: number | undefined;
                      let sellFixedBrk2: number | undefined;
                      let optPerLot2 = 0;
                      if (isOpt2) {
                        optPerLot2 = client?.brokerageRates?.option || DEFAULT_OPTION_BROKERAGE;
                        buyFixedBrk2 = optPerLot2 * sellLots2;
                        sellFixedBrk2 = optPerLot2 * sellLots2;
                      }
                      const rate2 = isFut ? CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE : 0;
                      const foBuy = calculateChargesBreakdown(tradeType2, buyValue, 0, rate2, false, posExch, buyFixedBrk2);
                      const foSell = calculateChargesBreakdown(tradeType2, 0, sellValue, rate2, false, posExch, sellFixedBrk2);
                      const foTotal = calculateChargesBreakdown(tradeType2, buyValue, sellValue, rate2, false, posExch, isOpt2 ? optPerLot2 * sellLots2 * 2 : undefined);
                      if (!foTotal || foTotal.totalCharges <= 0) return null;

                      const brkLbl2 = isOpt2
                        ? `Brokerage (₹${optPerLot2} × ${sellLots2} lots)`
                        : `Brokerage (${(CHARGE_RATES.DEFAULT_FUTURE_BROKERAGE * 100).toFixed(2).replace(/\.?0+$/, '')}%)`;
                      const sttLbl2 = isFut
                        ? `STT (${(CHARGE_RATES.STT_FUTURE_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell)`
                        : `STT (${(CHARGE_RATES.STT_OPTION_SELL * 100).toFixed(7).replace(/\.?0+$/, '')}% sell)`;
                      const exchRate2 = isFut ? CHARGE_RATES.EXCHANGE_FUTURE_NSE : CHARGE_RATES.EXCHANGE_OPTION;
                      const exchPct2 = (exchRate2 * 100).toFixed(5).replace(/\.?0+$/, '') + '%';
                      const sebiPct2 = (CHARGE_RATES.SEBI_RATE * 100).toFixed(4).replace(/\.?0+$/, '') + '%';
                      const stampRate2 = isFut ? CHARGE_RATES.STAMP_FUTURE : CHARGE_RATES.STAMP_OPTION;
                      const stampPct2 = (stampRate2 * 100).toFixed(7).replace(/\.?0+$/, '') + '%';
                      const gstPct2 = (CHARGE_RATES.GST_RATE * 100).toFixed(0) + '%';

                      return (
                        <Tooltip title={
                          <Box sx={{ fontSize: '0.7rem', lineHeight: 1.6 }}>
                            <div style={{ fontWeight: 700, marginBottom: 2 }}>{isOpt2 ? 'Options' : 'Futures'} — {sellLots2} lots ({qty} shares)</div>
                            <div><b>Buy side:</b></div>
                            <div>&nbsp; {brkLbl2}: ₹{foBuy.brokerage.toFixed(7)}</div>
                            <div>&nbsp; {sttLbl2}: ₹{foBuy.stt.toFixed(7)}</div>
                            <div>&nbsp; Exchange ({exchPct2}): ₹{foBuy.exchangeCharges.toFixed(7)}</div>
                            <div>&nbsp; SEBI ({sebiPct2}): ₹{foBuy.sebiCharges.toFixed(7)}</div>
                            <div>&nbsp; Stamp ({stampPct2} buy): ₹{foBuy.stampDuty.toFixed(7)}</div>
                            <div>&nbsp; GST ({gstPct2}): ₹{foBuy.gst.toFixed(7)}</div>
                            <div>&nbsp; <b>Buy Total: ₹{foBuy.totalCharges.toFixed(7)}</b></div>
                            <div style={{ marginTop: 4 }}><b>Sell side:</b></div>
                            <div>&nbsp; {brkLbl2}: ₹{foSell.brokerage.toFixed(7)}</div>
                            <div>&nbsp; {sttLbl2}: ₹{foSell.stt.toFixed(7)}</div>
                            <div>&nbsp; Exchange ({exchPct2}): ₹{foSell.exchangeCharges.toFixed(7)}</div>
                            <div>&nbsp; SEBI ({sebiPct2}): ₹{foSell.sebiCharges.toFixed(7)}</div>
                            <div>&nbsp; Stamp ({stampPct2} buy): ₹{foSell.stampDuty.toFixed(7)}</div>
                            <div>&nbsp; GST ({gstPct2}): ₹{foSell.gst.toFixed(7)}</div>
                            <div>&nbsp; <b>Sell Total: ₹{foSell.totalCharges.toFixed(7)}</b></div>
                            <div style={{ marginTop: 4, fontWeight: 700 }}>Total: ₹{foTotal.totalCharges.toFixed(7)}</div>
                          </Box>
                        } arrow placement="top">
                          <Typography variant="caption" sx={{ color: '#0A0A0A', fontWeight: 600, fontSize: '0.65rem', cursor: 'help' }}>
                            Est. Charges: ₹{foTotal.totalCharges.toFixed(7)}
                          </Typography>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                    </>);
                  })()}
                </Box>
              ) : (
                <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', borderTop: `1px solid ${COLORS.border}` }}>
                  <Button size="small"
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: COLORS.positive, border: `1px solid ${COLORS.positive}22`, borderRadius: 1.5, px: 1.5 }}
                    onClick={() => {
                      const eq = isEquityTradeType(positionDetail.tradeType);
                      setPrefillData({ clientId: positionDetail.clientId, scrip: positionDetail.scrip, tradeType: eq ? 'Equity' : positionDetail.tradeType === TradeType.FUTURE ? 'Future' : 'Option', direction: positionDetail.direction });
                      setCurrentBreakeven(positionDetail.breakeven);
                      setPositionDetail(null);
                      setShowTradeForm(true);
                    }}>
                    {positionDetail.direction === 'SELL' ? 'Add Short' : 'Buy'}
                  </Button>
                  <Button size="small"
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', color: positionDetail.direction === 'SELL' ? COLORS.positive : COLORS.negative, border: `1px solid ${positionDetail.direction === 'SELL' ? COLORS.positive : COLORS.negative}22`, borderRadius: 1.5, px: 1.5 }}
                    onClick={() => setLifoSellForm({ show: true, qty: '', price: '', loading: false, error: null })}>
                    {positionDetail.direction === 'SELL' ? 'Cover' : 'Sell'}
                  </Button>
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
